/**
 * ProfitVehicleDayMapModal — Vehicle/order map overlay extracted from ProfitabilityReport
 * 
 * Phase 1: Extract the map modal into a standalone component that can be rendered from
 * ProfitabilityReport or future profitability views without circular dependencies.
 * 
 * The modal owns all map-local UI state (playback, selection, suggest-route, fly-to, sidebar).
 * State resets via key prop when modal opens with different row/order.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  X,
  MapPin,
  Flag,
  PlayCircle,
  Package,
  Target,
  Check,
  Lightbulb,
  Truck,
} from 'lucide-react';
import {
  Map as MapComponent,
  MapControls,
  MapMarker,
  MarkerContent,
  MarkerTooltip,
  MarkerPopup,
  MapRoute,
  useMap,
} from '@/components/ui/map';
import { standardiseVehicleId } from '@/utils/vehicleIdStandardise';
import { authenticatedFetch } from '@/utils/authenticatedFetch';
import { suggestRouteOrder } from '@/utils/routeOptimization';
import {
  buildVehicleSuggestStops,
  haversinePathMiles,
  suggestStopCoordinatesForMap,
} from '@/utils/vehicleMapRouteSuggest';
import VehicleMapGpsPlayback from '@/components/profitability/VehicleMapGpsPlayback';
import {
  buildVehicleDayPlaybackTrack,
  formatUkPlaybackClock,
  formatUkPlaybackDate,
  withOrderProximityEvents,
} from '@/utils/vehicleDayPlaybackTrack';
import {
  orderRoleOnVehicleMap,
  resolveOrderHandoffMapPoint,
  type OrgDepotForMap,
  type OrderHandoffMapPoint,
} from '@/utils/mapOrderVehicleRole';
import {
  buildRouteSegmentSelectionId,
  orderMapSegmentSelectionId,
  mapSegmentIdsBetweenVisibleStops,
  resolveTrunkTelematicsStartAnchor,
  buildRouteAnchorText,
  calculatePolylineDistanceMiles,
  buildOrderRouteLegLabel,
  buildOrderRouteSegments,
  collectionOnlyRouteSegments,
  orderLegClipEnd,
  resolveOrderRouteSegmentsForMap,
  clipOrderRouteSegmentsToOrderWindow,
  segmentCoordinatesForMapRoute,
  extractOrderLegRouteSegment,
  mergeOrderLegTelematicsNearAnchors,
  buildSyntheticOrderLegSegment,
} from '@/utils/profitVehicleDayMapRoutes';
import {
  getDateOnly,
  normalizeTelematicsDate,
  orderGeocodeMapPoint,
  HandoffMapIcon,
  isTelematicsRouteAnchor,
  buildDeliveryStopSidebarState,
  buildMapSidebarStopRows,
  type ProfitMapOrder,
  type TelematicsDeliveryStop,
  type RouteSegment,
  type SyntheticOrderHandoffStop,
  orderCollectedByLabel,
  orderDeliveredByOtherDepot,
} from '@/utils/profitVehicleDayMap';
import type { ProfitabilityConfig } from '@/contexts/ConfigContext';
import { normaliseBodyStyleClass, normaliseTonnageClass } from '@/utils/costRateBasis';
import { STATUS_COLORS, CHART_PALETTE } from '@/config/colors';

// ============================================================================
// Types
// ============================================================================

export interface ProfitVehicleDayMapModalProps {
  /** Row from Daily Summary table (or order-derived equivalent) */
  row: {
    vehicleId: string;
    date: string;
    periodLabel?: string;
    orderIds?: string[];
    pickupOrderIds?: string[];
    deliveryOrderIds?: string[];
    startDate?: string;
    endDate?: string;
  };
  /** When set, modal opened from order row (show origin/destination instead of vehicle route) */
  order: ProfitMapOrder | null;
  /** All delivery stops for filtering */
  stops: TelematicsDeliveryStop[];
  /** All route segments for filtering */
  segments: RouteSegment[];
  /** Org depots for handoff pins */
  orgDepots: OrgDepotForMap[];
  /** All order records for lookup (sidebar, handoff resolution, etc.) */
  orders: ProfitMapOrder[];
  /** Order coords not filled yet: the server's postcode lookup was still loading. */
  orderCoordsPending?: boolean;
  /** Merged costs (for handoff depot resolution from truth revenue_breakdown) */
  mergedCosts: Array<{
    date: string;
    vehicle: string;
    revenue_breakdown?: Array<{
      order_entity_id: string;
      order_name?: string;
      handoff_depot?: string;
    }>;
  }>;
  /** Profitability config (for fuel cost calculations). Optional — map works without it. */
  config?: ProfitabilityConfig | null;
  /** Vehicle ID → type string (e.g. "Lorry", "Tractor Unit") for fuel cost lookup */
  vehicleIdToType?: Record<string, string>;
  /** Vehicle ID → tonnage class for tonnage-based fuel cost */
  vehicleIdToTonnage?: Record<string, string>;
  /** Telematics mileage map: "vehicleId|YYYY-MM-DD" → daily_miles record */
  telematicsMileageMap?: Map<string, { daily_miles: number }>;
  /**
   * When set, empty stops/segments came from a failed fetch — not confirmed empty GPS.
   * Shown instead of the “No GPS data found” copy.
   */
  telematicsLoadError?: string | null;
  /** Close handler */
  onClose: () => void;
}

// ============================================================================
// Helper functions (modal-local)
// ============================================================================

/** Fuel cost per mile based on vehicle type and config basis */
function getFuelCostPerMile(
  vehicleType: string,
  config: ProfitabilityConfig,
): number {
  const basis = config.costRateBasis ?? 'body_style';
  if (basis === 'tonnage') {
    const key = normaliseTonnageClass(vehicleType);
    if (key === '3.5T') return config.fuelCost3_5T;
    if (key === '7.5T') return config.fuelCost7_5T;
    if (key === '18T') return config.fuelCost18T;
    if (key === '26T') return config.fuelCost26T;
    if (key === 'Artic') return config.fuelCostArtic;
    return config.fuelCostDefault;
  }
  const normalised = (vehicleType || '').trim();
  if (/^lorry$/i.test(normalised)) return config.fuelCostLorry;
  if (/^tractor\s*unit$/i.test(normalised)) return config.fuelCostTractorUnit;
  if (/^mini\s*truck$/i.test(normalised)) return config.fuelCostMiniTruck;
  if (/^service\s*van$/i.test(normalised)) return config.fuelCostServiceVan;
  if (/^rigid\s*truck$/i.test(normalised)) return config.fuelCostRigidTruck;
  return config.fuelCostDefault;
}

/** Resolve vehicle cost class label (for fuel cost lookup) */
function resolveVehicleCostClassLabel(
  stdVid: string,
  vehicleIdToType: Record<string, string>,
  vehicleIdToTonnage: Record<string, string>,
  config: ProfitabilityConfig,
  storedCostClass?: string,
): string {
  const basis = config.costRateBasis ?? 'body_style';

  const fromConfig = (): string | null => {
    if (basis === 'tonnage') {
      const raw = vehicleIdToTonnage[stdVid] || vehicleIdToType[stdVid];
      if (!raw || /^default$/i.test(raw.trim())) return null;
      const key = normaliseTonnageClass(raw);
      return key === 'Default' ? null : key;
    }
    const raw = vehicleIdToType[stdVid];
    if (!raw || /^default$/i.test(raw.trim())) return null;
    const key = normaliseBodyStyleClass(raw);
    return key === 'Default' ? null : key;
  };

  const stored = storedCostClass?.trim();
  if (stored && !/^default$/i.test(stored)) {
    if (basis === 'tonnage') {
      const key = normaliseTonnageClass(stored);
      if (key !== 'Default') return key;
    } else {
      const key = normaliseBodyStyleClass(stored);
      if (key !== 'Default') return key;
    }
  }

  return fromConfig() ?? 'Default';
}


// ============================================================================
// Map Controller (auto-fit bounds, fly-to)
// ============================================================================

/** Helper to compute bounds from stops + segments + orderPoints */
function computeMapBounds(
  stops: TelematicsDeliveryStop[],
  segments: RouteSegment[],
  orderPoints?: Array<{ lat: number; lon: number }>,
): [[number, number], [number, number]] | null {
  const allPoints: Array<{ lat: number; lon: number }> = [];
  const validStops = stops.filter(
    (s) => s.latitude && s.longitude && s.latitude !== 0 && s.longitude !== 0,
  );
  validStops.forEach((s) => allPoints.push({ lat: s.latitude, lon: s.longitude }));
  segments.forEach((seg) => {
    (seg.points || []).forEach(([lat, lon]) => {
      if (lat != null && lon != null && lat !== 0 && lon !== 0) allPoints.push({ lat, lon });
    });
  });
  if (orderPoints?.length) orderPoints.forEach((p) => allPoints.push(p));
  if (allPoints.length === 0) return null;
  const lats = allPoints.map((p) => p.lat);
  const lons = allPoints.map((p) => p.lon);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const latPadding = (maxLat - minLat) * 0.1 || 0.01;
  const lonPadding = (maxLon - minLon) * 0.1 || 0.01;
  return [
    [minLon - lonPadding, minLat - latPadding],
    [maxLon + lonPadding, maxLat + latPadding],
  ] as [[number, number], [number, number]];
}

interface MapControllerProps {
  stops: TelematicsDeliveryStop[];
  segments: RouteSegment[];
  mapDataKey?: string;
  orderPoints?: Array<{ lat: number; lon: number }>;
  fittedKeyRef?: React.MutableRefObject<string | null>;
  flyToPoint?: { lat: number; lon: number } | null;
  onFlyToDone?: () => void;
  skipFlyToStopRef?: React.MutableRefObject<boolean>;
  lastSkippedFlyToStopIdRef?: React.MutableRefObject<string | null>;
  fitBoundsTrigger?: number;
  lastProcessedFitBoundsTriggerRef?: React.MutableRefObject<number>;
  selectedStopId?: string | null;
}

const MapController: React.FC<MapControllerProps> = ({
  stops,
  segments,
  mapDataKey,
  orderPoints,
  fittedKeyRef,
  flyToPoint,
  onFlyToDone,
  skipFlyToStopRef,
  lastSkippedFlyToStopIdRef,
  fitBoundsTrigger,
  lastProcessedFitBoundsTriggerRef,
  selectedStopId,
}) => {
  const { map, isLoaded } = useMap();
  const flownToStopIdRef = useRef<string | null>(null);

  // Fly to point when ORDER JOURNEY Pickup/Delivery card is clicked
  useEffect(() => {
    if (!isLoaded || !map || !flyToPoint || !Number.isFinite(flyToPoint.lat) || !Number.isFinite(flyToPoint.lon))
      return;
    map.flyTo({
      center: [flyToPoint.lon, flyToPoint.lat],
      zoom: 15,
      duration: 800,
      essential: true,
    });
    onFlyToDone?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, map, flyToPoint]);

  // Fly to selected stop only once when user first selects it
  useEffect(() => {
    if (!isLoaded || !map) return;
    if (skipFlyToStopRef?.current) {
      if (selectedStopId) flownToStopIdRef.current = selectedStopId;
      skipFlyToStopRef.current = false;
      return;
    }
    if (lastSkippedFlyToStopIdRef?.current === selectedStopId) {
      flownToStopIdRef.current = selectedStopId ?? null;
      return;
    }
    if (flyToPoint && Number.isFinite(flyToPoint.lat) && Number.isFinite(flyToPoint.lon)) return;
    if (!selectedStopId) {
      flownToStopIdRef.current = null;
      return;
    }
    if (flownToStopIdRef.current === selectedStopId) return;

    const selectedStop = stops.find((stop) => {
      const stopId = `stop-${stop.AssetName}-${stop.day_date}-${stop.stop_number}`;
      return stopId === selectedStopId;
    });

    if (selectedStop && selectedStop.latitude && selectedStop.longitude) {
      map.flyTo({
        center: [selectedStop.longitude, selectedStop.latitude],
        zoom: Math.max(map.getZoom(), 12),
        duration: 1000,
        essential: true,
      });
      flownToStopIdRef.current = selectedStopId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, map, selectedStopId, flyToPoint]);

  // Fit bounds when trigger increments
  useEffect(() => {
    if (
      !isLoaded ||
      !map ||
      !mapDataKey ||
      fitBoundsTrigger == null ||
      fitBoundsTrigger <= 0 ||
      !lastProcessedFitBoundsTriggerRef
    )
      return;
    if (lastProcessedFitBoundsTriggerRef.current === fitBoundsTrigger) return;
    lastProcessedFitBoundsTriggerRef.current = fitBoundsTrigger;
    flownToStopIdRef.current = null;

    const bounds = computeMapBounds(stops, segments, orderPoints);
    if (!bounds) return;
    map.fitBounds(bounds, { padding: { top: 50, bottom: 50, left: 50, right: 50 }, duration: 500 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, map, mapDataKey, fitBoundsTrigger]);

  // Initial bounds fitting
  useEffect(() => {
    if (!isLoaded || !map || !mapDataKey || !fittedKeyRef) return;
    if (fittedKeyRef.current === mapDataKey) return;

    const bounds = computeMapBounds(stops, segments, orderPoints);
    if (!bounds) return;
    map.fitBounds(bounds, { padding: { top: 50, bottom: 50, left: 50, right: 50 }, duration: 500 });
    fittedKeyRef.current = mapDataKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, map, mapDataKey, fittedKeyRef]);

  return null;
};

// ============================================================================
// Main Modal Component
// ============================================================================

export const ProfitVehicleDayMapModal: React.FC<ProfitVehicleDayMapModalProps> = ({
  row,
  order,
  stops,
  segments,
  orgDepots,
  orders,
  orderCoordsPending = false,
  mergedCosts,
  config = null,
  vehicleIdToType = {},
  vehicleIdToTonnage = {},
  telematicsMileageMap = new Map(),
  telematicsLoadError = null,
  onClose,
}) => {
  // ===== Map-local UI state (resets on key change) =====
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [selectedMapSegmentIds, setSelectedMapSegmentIds] = useState<Set<string> | null>(null);
  const [suggestedRouteResult, setSuggestedRouteResult] = useState<{
    order: number[];
    segmentMiles: number[];
    totalMiles: number;
  } | null>(null);
  const [showSuggestedOnMap, setShowSuggestedOnMap] = useState(false);
  const [suggestRouteLoading, setSuggestRouteLoading] = useState(false);
  const [suggestedRoadGeometry, setSuggestedRoadGeometry] = useState<[number, number][] | null>(null);
  const [suggestedRoadRouteLoading, setSuggestedRoadRouteLoading] = useState(false);
  const [suggestedRoadRouteError, setSuggestedRoadRouteError] = useState(false);
  const [suggestedRoadRouteHint, setSuggestedRoadRouteHint] = useState<string | null>(null);
  const [allowStraightLineFallback, setAllowStraightLineFallback] = useState(true);
  const [mapPlaybackPos, setMapPlaybackPos] = useState<{
    lat: number;
    lon: number;
    timestamp: string;
    trail: [number, number][];
  } | null>(null);
  const [mapOrderSpotSelected, setMapOrderSpotSelected] = useState<string | null>(null);
  const [expandedOrderIdInMapSidebar, setExpandedOrderIdInMapSidebar] = useState<string | null>(null);
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);
  const [copiedTrackingIds, setCopiedTrackingIds] = useState<Set<string>>(new Set());
  const [sidebarOrderRoleFilter, setSidebarOrderRoleFilter] = useState<'all' | 'pickup' | 'delivery'>('all');
  // Outbound job delivered by another depot: our vehicle only collected it.
  const deliveredByOtherDepot = orderDeliveredByOtherDepot(order);
  const [collapsedHandoffGroups, setCollapsedHandoffGroups] = useState<Record<string, boolean>>({});

  // Map controller refs (survive remounts)
  const mapModalFittedKeyRef = useRef<string | null>(null);
  const [mapFlyToPoint, setMapFlyToPoint] = useState<{ lat: number; lon: number } | null>(null);
  const skipFlyToStopRef = useRef(false);
  const lastSkippedFlyToStopIdRef = useRef<string | null>(null);
  const [mapFitBoundsTrigger, setMapFitBoundsTrigger] = useState(0);
  const lastProcessedFitBoundsTriggerRef = useRef(0);
  const previousSelectedStopIdRef = useRef<string | null>(null);
  const prevSelectedStopIdRef = useRef<string | null>(null);

  // Clipboard helpers
  const copyTextToClipboard = useCallback(async (value: string | null | undefined) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedTrackingIds((prev) => new Set(prev).add(value));
    } catch {
      // No-op
    }
  }, []);

  const copyOrderIdToClipboard = useCallback(
    async (orderId: string | null | undefined) => {
      await copyTextToClipboard(orderId);
    },
    [copyTextToClipboard],
  );

  // Clear copied IDs after timeout
  useEffect(() => {
    if (copiedTrackingIds.size === 0) return;
    const timeout = window.setTimeout(() => {
      setCopiedTrackingIds((prev) => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        next.clear();
        return next;
      });
    }, 1200);
    return () => window.clearTimeout(timeout);
  }, [copiedTrackingIds]);

  // Fit bounds when stop deselected
  useEffect(() => {
    if (selectedStopId != null) {
      previousSelectedStopIdRef.current = selectedStopId;
      return;
    }
    if (previousSelectedStopIdRef.current != null) {
      setMapFitBoundsTrigger((t) => t + 1);
      previousSelectedStopIdRef.current = null;
    }
  }, [selectedStopId]);

  // Clear skip fly-to when stop changes
  useEffect(() => {
    if (prevSelectedStopIdRef.current !== selectedStopId) {
      prevSelectedStopIdRef.current = selectedStopId;
      lastSkippedFlyToStopIdRef.current = null;
    }
  }, [selectedStopId]);

  // Playback position handler
  const handleMapPlaybackPosition = useCallback(
    (pos: { lat: number; lon: number; timestamp: string; trail: [number, number][] } | null) => {
      setMapPlaybackPos(pos);
    },
    [],
  );

  // Helper: resolve vehicle cost class for fuel cost lookup
  const resolveVehicleCostClassLabelLocal = useCallback(
    (stdVid: string, storedCostClass?: string): string => {
      if (!config) return storedCostClass?.trim() || 'Default';
      return resolveVehicleCostClassLabel(stdVid, vehicleIdToType, vehicleIdToTonnage, config, storedCostClass);
    },
    [vehicleIdToType, vehicleIdToTonnage, config],
  );

  // Helper: get telematics miles for date range
  const getTelematicsMilesForRange = useCallback(
    (vehicleId: string, startDate: string, endDate: string): number => {
      if (!startDate || !endDate || startDate > endDate) return 0;
      let total = 0;
      const d = new Date(startDate + 'T12:00:00');
      const end = new Date(endDate + 'T12:00:00');
      while (d <= end) {
        const key = `${vehicleId}|${d.toISOString().slice(0, 10)}`;
        const rec = telematicsMileageMap.get(key);
        if (rec) total += rec.daily_miles ?? 0;
        d.setDate(d.getDate() + 1);
      }
      return total;
    },
    [telematicsMileageMap],
  );

  // ===== Modal logic (extracted from IIFE) =====
        // Get stops and route segments for this vehicle/date or vehicle/date range (week or month)
        const modalVehicleId = standardiseVehicleId(row.vehicleId);
        const startDate = row.startDate ?? (normalizeTelematicsDate(row.date) || getDateOnly(row.date));
        const endDate = row.endDate ?? startDate;
        const isDateRange = startDate !== endDate;
        const modalVehicleIdsForMap = new Set<string>();
        if (modalVehicleId) modalVehicleIdsForMap.add(modalVehicleId);
        if (order) {
          const pickupVehicleId = standardiseVehicleId(order.vehicle_pickup ?? '');
          const observedVehicleId = standardiseVehicleId(order.vehicle_observed ?? order.vehicleId ?? '');
          const assignedVehicleId = standardiseVehicleId(order.vehicle_assigned ?? '');
          if (pickupVehicleId) modalVehicleIdsForMap.add(pickupVehicleId);
          if (observedVehicleId) modalVehicleIdsForMap.add(observedVehicleId);
          if (assignedVehicleId) modalVehicleIdsForMap.add(assignedVehicleId);
        }

        const orderRole = order
          ? orderRoleOnVehicleMap(order, row.vehicleId, new Set((row.pickupOrderIds ?? []).map((id) => String(id).trim()).filter(Boolean)), new Set((row.deliveryOrderIds ?? []).map((id) => String(id).trim()).filter(Boolean)))
          : null;

        // Order journey view: always show destination when geocoded (not gated on
        // modal vehicle role — order click opens as delivery plate but still needs End).
        const orderDeliveryPoint = order
          ? orderGeocodeMapPoint(order, 'destination')
          : null;
        const orderPickupPoint = order
          ? orderGeocodeMapPoint(order, 'origin')
          : null;
        let orderHandoffPoint: OrderHandoffMapPoint | null = null;

        const modalStops: any[] = order
          ? []
          : stops.filter(stop => {
              const stopVid = standardiseVehicleId(stop.AssetName);
              const stopDate = normalizeTelematicsDate(stop.day_date);
              if (!modalVehicleIdsForMap.has(stopVid)) return false;
              if (!stop.latitude || !stop.longitude || stop.latitude === 0 || stop.longitude === 0) return false;
              if (isDateRange) return stopDate >= startDate && stopDate <= endDate;
              return stopDate === startDate;
            });
        const modalSegments: any[] = order
          ? resolveOrderRouteSegmentsForMap(order, segments, startDate)
          : segments.filter(seg => {
              const segVid = standardiseVehicleId(seg.vehicle);
              const segDate = normalizeTelematicsDate(seg.date);
              if (!modalVehicleIdsForMap.has(segVid)) return false;
              if (isDateRange) return segDate >= startDate && segDate <= endDate;
              return segDate === startDate;
            });

        // Use telematics daily miles: single day from map; range from getTelematicsMilesForRange
        const telematicsMilesRecord = !isDateRange ? telematicsMileageMap.get(`${modalVehicleId}|${startDate}`) : null;
        const telematicsMilesForRange = isDateRange ? getTelematicsMilesForRange(modalVehicleId, startDate, endDate) : 0;

        // Sort stops by date then stop_number (for date range: group by day, then order within day)
        let sortedStops = [...(modalStops as any[])].sort((a: any, b: any) => {
          const dateA = normalizeTelematicsDate(a.day_date);
          const dateB = normalizeTelematicsDate(b.day_date);
          if (dateA !== dateB) return (dateA || '').localeCompare(dateB || '');
          return (a.stop_number ?? 0) - (b.stop_number ?? 0);
        });

        const pickupOrderIdSet = new Set(
          (row.pickupOrderIds ?? []).map((id) => String(id).trim()).filter(Boolean),
        );
        const deliveryOrderIdSet = new Set(
          (row.deliveryOrderIds ?? []).map((id) => String(id).trim()).filter(Boolean),
        );
        const modalVehicleForRole = row.vehicleId;

        /** Truth getnotes depot → org depot pin (never End proximity). */
        const resolveHandoffDepotNumber = (
          orderId: string,
          preferred?: string | null,
        ): string | undefined => {
          const fromPreferred = String(preferred ?? '').trim();
          if (fromPreferred) return fromPreferred;
          const oid = String(orderId ?? '').trim();
          if (!oid) return undefined;
          for (const cost of mergedCosts) {
            if (standardiseVehicleId(cost.vehicle) !== modalVehicleId) continue;
            const costDate = (cost.date || '').slice(0, 10);
            if (isDateRange) {
              if (costDate < startDate || costDate > endDate) continue;
            } else if (costDate !== startDate) {
              continue;
            }
            for (const b of cost.revenue_breakdown ?? []) {
              if (
                (b.order_name && b.order_name === oid)
                || b.order_entity_id === oid
              ) {
                const depot = String(b.handoff_depot ?? '').trim();
                if (depot) return depot;
              }
            }
          }
          return undefined;
        };

        // Handoff: pickup-role Daily Summary, or multi-leg order journey (hub between plates).
        const pickupVidForOrder = order
          ? standardiseVehicleId(order.vehicle_pickup ?? '')
          : '';
        const deliveryVidForOrder = order
          ? standardiseVehicleId(
            order.vehicle_observed ?? order.vehicleId ?? '',
          )
          : '';
        const isMultiLegOrder = Boolean(
          pickupVidForOrder && deliveryVidForOrder && pickupVidForOrder !== deliveryVidForOrder,
        );

        if (order) {
          if (orderRole === 'pickup' || isMultiLegOrder || deliveredByOtherDepot) {
            orderHandoffPoint = resolveOrderHandoffMapPoint(
              order.orderId,
              orgDepots,
              resolveHandoffDepotNumber(order.orderId, order.handoff_depot),
            );
          }
        }

        // Green Start = trunk telematics start (not collection geocode). End stays delivery destination.
        const trunkStartAnchor = order && pickupVidForOrder
          ? resolveTrunkTelematicsStartAnchor({
            pickupVehicleId: pickupVidForOrder,
            originDate:
              order.origin_end_timestamp_local
              ?? order.origin_end_timestamp
              ?? startDate,
            destinationDate:
              order.destination_end_timestamp_local
              ?? order.destination_timestamp
              ?? startDate,
            stops: stops,
            trunkSegments: modalSegments.filter(
              (seg) => standardiseVehicleId((seg as any).vehicle) === pickupVidForOrder,
            ) as any[],
          })
          : null;
        const orderMapStartPoint = trunkStartAnchor
          ? { lat: trunkStartAnchor.lat, lon: trunkStartAnchor.lon }
          : orderPickupPoint;
        const orderMapStartVehicle = trunkStartAnchor?.vehicleId
          || pickupVidForOrder
          || standardiseVehicleId(row.vehicleId)
          || row.vehicleId;
        const orderMapStartDate = trunkStartAnchor?.day_date || startDate;

        const orderRouteStops: any[] = order ? [
          ...(orderMapStartPoint ? [{ id: 'order-start', label: buildOrderRouteLegLabel('start'), latitude: orderMapStartPoint.lat, longitude: orderMapStartPoint.lon, stop_type: 'Start', stop_number: 1, AssetName: orderMapStartVehicle, day_date: orderMapStartDate, arrival_time: null, departure_time: null, duration_minutes: 0, postcode: '', miles_to_next_stop: 0 }] : []),
          ...(orderHandoffPoint ? [{ id: 'order-handoff', label: 'Handoff', latitude: orderHandoffPoint.lat, longitude: orderHandoffPoint.lon, stop_type: 'Handoff', stop_number: 2, AssetName: orderMapStartVehicle, day_date: orderMapStartDate, arrival_time: null, departure_time: null, duration_minutes: 0, postcode: orderHandoffPoint.postcode ?? '', miles_to_next_stop: 0 }] : []),
          // Order map uses the amber Delivery Target pin for destination — no separate red End flag.
        ] : [];

        if (order) {
          modalStops.splice(0, modalStops.length, ...orderRouteStops);
          // Rebuild after inject — earlier snapshot is empty and breaks Start/End markers.
          sortedStops = [...(modalStops as any[])].sort((a: any, b: any) => {
            const dateA = normalizeTelematicsDate(a.day_date);
            const dateB = normalizeTelematicsDate(b.day_date);
            if (dateA !== dateB) return (dateA || '').localeCompare(dateB || '');
            return (a.stop_number ?? 0) - (b.stop_number ?? 0);
          });

          const pickupVid = pickupVidForOrder;
          const deliveryVid = deliveryVidForOrder;
          const isMultiLeg = isMultiLegOrder;

          if (isMultiLeg) {
            // Keep both trunk and delivery telematics days — do not collapse to the
            // modal header plate (often the delivery vehicle only).
            const bothLegs = modalSegments
              .filter((seg) => {
                const vid = standardiseVehicleId(seg.vehicle);
                return vid === pickupVid || vid === deliveryVid;
              })
              .map((seg) => {
                const vid = standardiseVehicleId(seg.vehicle);
                // Delivered by another depot: our first carrier collected it and
                // the second took it on to the hub — neither delivered it.
                const label =
                  vid === pickupVid
                    ? (deliveredByOtherDepot ? 'Collection leg' : 'Trunk leg')
                    : vid === deliveryVid
                      ? (deliveredByOtherDepot ? 'Trunk leg' : 'Delivery leg')
                      : (seg.label || 'Route');
                return { ...seg, label };
              });
            if (bothLegs.length > 0) {
              modalSegments.splice(0, modalSegments.length, ...bothLegs);
            }
          } else {
            // Sole carrier: prefer this modal vehicle's telematics when present.
            const ownSegments = modalSegments.filter(
              (seg) => standardiseVehicleId(seg.vehicle) === modalVehicleId,
            );
            if (ownSegments.length > 0) {
              modalSegments.splice(0, modalSegments.length, ...ownSegments);
            }
          }

          // Prefer the actual vehicle-day telematics route when it exists. Synthetic
          // order legs are only a fallback for orders with no route geometry.
          if (modalSegments.length === 0) {
            const fallbackLegs = buildOrderRouteSegments(orderPickupPoint, orderHandoffPoint, orderDeliveryPoint);
            modalSegments.splice(
              0,
              modalSegments.length,
              ...(deliveredByOtherDepot ? collectionOnlyRouteSegments(fallbackLegs) : fallbackLegs),
            );
          } else if (isMultiLeg && orderHandoffPoint && !deliveredByOtherDepot) {
            // One Trunk leg (Start/pickup → hub) + one Delivery leg (hub → End).
            // Drop later vehicle-day loops that only brush the corridor.
            const trunkSegs = modalSegments.filter(
              (seg) => standardiseVehicleId(seg.vehicle) === pickupVid,
            );
            const deliverySegs = modalSegments.filter(
              (seg) => standardiseVehicleId(seg.vehicle) === deliveryVid,
            );
            const handoffPt = { lat: orderHandoffPoint.lat, lon: orderHandoffPoint.lon };
            const trunkFrom = orderMapStartPoint ?? orderPickupPoint;
            const trunkLeg = extractOrderLegRouteSegment(
              trunkSegs,
              trunkFrom,
              handoffPt,
              { label: 'Trunk leg', vehicle: pickupVid },
            ) ?? (
              trunkFrom
                ? buildSyntheticOrderLegSegment(trunkFrom, handoffPt, {
                  label: 'Trunk leg',
                  vehicle: pickupVid,
                  id: 'order-trunk-leg',
                })
                : null
            );

            let deliveryLeg = extractOrderLegRouteSegment(
              deliverySegs,
              handoffPt,
              orderDeliveryPoint,
              { label: 'Delivery leg', vehicle: deliveryVid },
            );
            if (!deliveryLeg && orderDeliveryPoint) {
              const clippedDelivery = clipOrderRouteSegmentsToOrderWindow(
                deliverySegs,
                handoffPt,
                orderDeliveryPoint,
              );
              deliveryLeg = extractOrderLegRouteSegment(
                clippedDelivery as typeof deliverySegs,
                handoffPt,
                orderDeliveryPoint,
                { label: 'Delivery leg', vehicle: deliveryVid },
              )
                ?? mergeOrderLegTelematicsNearAnchors(
                  deliverySegs,
                  handoffPt,
                  orderDeliveryPoint,
                  { label: 'Delivery leg', vehicle: deliveryVid },
                )
                ?? mergeOrderLegTelematicsNearAnchors(
                  clippedDelivery as typeof deliverySegs,
                  handoffPt,
                  orderDeliveryPoint,
                  { label: 'Delivery leg', vehicle: deliveryVid },
                )
                ?? buildSyntheticOrderLegSegment(handoffPt, orderDeliveryPoint, {
                  label: 'Delivery leg',
                  vehicle: deliveryVid,
                  id: 'order-delivery-leg',
                });
            }

            const condensed = [trunkLeg, deliveryLeg].filter(Boolean) as typeof modalSegments;
            if (condensed.length > 0) {
              modalSegments.splice(0, modalSegments.length, ...condensed);
            } else {
              modalSegments.splice(
                0,
                modalSegments.length,
                ...clipOrderRouteSegmentsToOrderWindow(trunkSegs, trunkFrom, handoffPt),
                ...clipOrderRouteSegmentsToOrderWindow(deliverySegs, handoffPt, orderDeliveryPoint),
              );
            }
          } else if (!(isMultiLeg && deliveredByOtherDepot)) {
            // Delivered by another depot: our leg ends at the handoff depot, not
            // at the customer's address.
            const clipEndPoint = orderLegClipEnd(deliveredByOtherDepot, orderHandoffPoint, orderDeliveryPoint);
            const legFrom = orderMapStartPoint ?? orderPickupPoint;
            const singleLeg = extractOrderLegRouteSegment(
              modalSegments,
              legFrom,
              clipEndPoint,
              {
                label: deliveredByOtherDepot
                  ? 'Collection leg'
                  : (orderHandoffPoint && !orderDeliveryPoint ? 'Trunk leg' : 'Delivery leg'),
                vehicle: modalVehicleId,
              },
            );
            if (singleLeg) {
              modalSegments.splice(0, modalSegments.length, singleLeg);
            } else {
              modalSegments.splice(
                0,
                modalSegments.length,
                ...clipOrderRouteSegmentsToOrderWindow(modalSegments, legFrom, clipEndPoint),
              );
            }
          }
        }

        // Customer delivery pin only when this vehicle is the delivery leg (not pick-up/trunk/unknown).
        // Sort segments by date then from_stop_number (for map polylines; when range, order by day then stop order)
        const sortedSegments = [...(modalSegments as any[])].sort((a: any, b: any) => {
          const dateA = normalizeTelematicsDate(a.date);
          const dateB = normalizeTelematicsDate(b.date);
          if (dateA !== dateB) return (dateA || '').localeCompare(dateB || '');
          return a.from_stop_number - b.from_stop_number;
        });

        const routeContextStops = order ? modalStops : sortedStops;
        const routeContextSegments = order ? modalSegments : sortedSegments;

        // Daily Summary: split order pins by this vehicle's pick-up vs delivery role.
        const { vehicleDateOrderDestinations, vehicleDateOrderHandoffs } = (() => {
          const ids = row.orderIds ?? [];
          const destinations: Array<{ orderId: string; lat: number; lon: number; city?: string; postcode?: string; approximate?: boolean }> = [];
          const handoffs: OrderHandoffMapPoint[] = [];
          if (ids.length === 0) return { vehicleDateOrderDestinations: destinations, vehicleDateOrderHandoffs: handoffs };
          const idSet = new Set(ids);
          const seenDest = new Set<string>();
          const seenHand = new Set<string>();
          for (const r of orders) {
            if (!idSet.has(r.orderId)) continue;
            const role = orderRoleOnVehicleMap(r, modalVehicleForRole, pickupOrderIdSet, deliveryOrderIdSet);
            if (role === 'pickup') {
              if (seenHand.has(r.orderId)) continue;
              const hp = resolveOrderHandoffMapPoint(
                r.orderId,
                orgDepots,
                resolveHandoffDepotNumber(r.orderId, r.handoff_depot),
              );
              if (hp) {
                seenHand.add(r.orderId);
                handoffs.push(hp);
              }
              continue;
            }
            // Only explicit delivery legs get a customer destination pin (unknown → none).
            if (role !== 'delivery') continue;
            if (seenDest.has(r.orderId)) continue;
            const pt = orderGeocodeMapPoint(r, 'destination');
            if (!pt) continue;
            seenDest.add(r.orderId);
            destinations.push({
              orderId: r.orderId,
              lat: pt.lat,
              lon: pt.lon,
              city: r.destination_city,
              postcode: r.destination_postal_code,
              approximate: r.destination_coords_source === 'postcode_centroid',
            });
          }
          return { vehicleDateOrderDestinations: destinations, vehicleDateOrderHandoffs: handoffs };
        })();

        const orderPoints: Array<{ lat: number; lon: number }> | undefined = order ? (() => {
          const pts: Array<{ lat: number; lon: number }> = [];
          if (orderDeliveryPoint) pts.push({ lat: orderDeliveryPoint.lat, lon: orderDeliveryPoint.lon });
          if (orderHandoffPoint) pts.push({ lat: orderHandoffPoint.lat, lon: orderHandoffPoint.lon });
          if (orderMapStartPoint) pts.push({ lat: orderMapStartPoint.lat, lon: orderMapStartPoint.lon });
          else if (orderPickupPoint) pts.push({ lat: orderPickupPoint.lat, lon: orderPickupPoint.lon });
          return pts.length > 0 ? pts : undefined;
        })() : (() => {
          const pts: Array<{ lat: number; lon: number }> = [];
          for (const d of vehicleDateOrderDestinations) pts.push({ lat: d.lat, lon: d.lon });
          for (const h of vehicleDateOrderHandoffs) pts.push({ lat: h.lat, lon: h.lon });
          return pts.length > 0 ? pts : undefined;
        })();

        // Delivery destinations for STOPS tagging / proximity — delivery-leg only.
        const deliveryDestinationsForStops: Array<{
          orderId: string;
          lat: number;
          lon: number;
          postcode?: string;
          city?: string;
        }> = (() => {
          if (order && orderDeliveryPoint) {
            return [{
              orderId: order.orderId,
              lat: orderDeliveryPoint.lat,
              lon: orderDeliveryPoint.lon,
              postcode: order.destination_postal_code,
              city: order.destination_city,
            }];
          }
          return vehicleDateOrderDestinations.map((d) => ({
            orderId: d.orderId,
            lat: d.lat,
            lon: d.lon,
            postcode: d.postcode,
            city: d.city,
          }));
        })();
        const { deliveryStopIds, deliveryStopToOrderIds, unmatchedDeliveries } =
          buildDeliveryStopSidebarState(sortedStops, deliveryDestinationsForStops);

        // Same stop set as STOPS sidebar (anchors + order-matched) — replay / suggest must not treat GPS dwells as first-class.
        const visibleStopsForMap: TelematicsDeliveryStop[] = order
          ? (sortedStops as TelematicsDeliveryStop[])
          : (sortedStops as TelematicsDeliveryStop[]).filter((stop) => {
              if (isTelematicsRouteAnchor(stop, sortedStops as TelematicsDeliveryStop[])) return true;
              const id = `stop-${stop.AssetName}-${stop.day_date}-${stop.stop_number}`;
              return deliveryStopIds.has(id);
            });

        const segmentsForSidebar = order
          ? routeContextSegments.map((segment, index) => {
              const routePoints = Array.isArray((segment as any).points)
                ? ((segment as any).points as Array<[number, number]>)
                : Array.isArray((segment as any).coordinates)
                  ? ((segment as any).coordinates as Array<[number, number]>).map(([lon, lat]) => [lat, lon] as [number, number])
                  : [];
              const id = orderMapSegmentSelectionId(segment as any, index);
              return {
                id,
                mapSegmentIds: [id],
                from_stop_number: (segment as any).from_stop_number ?? index + 1,
                to_stop_number: (segment as any).to_stop_number ?? index + 2,
                distance: calculatePolylineDistanceMiles(routePoints),
                label: (segment as any).label ?? 'Segment',
              };
            })
          : (() => {
              const stopsForSegments = routeContextStops.filter((stop) => {
                if (isTelematicsRouteAnchor(stop as TelematicsDeliveryStop, routeContextStops as TelematicsDeliveryStop[])) return true;
                const id = `stop-${(stop as TelematicsDeliveryStop).AssetName}-${(stop as TelematicsDeliveryStop).day_date}-${(stop as TelematicsDeliveryStop).stop_number}`;
                return deliveryStopIds.has(id);
              });
              if (stopsForSegments.length < 2) return [];
              return stopsForSegments.slice(0, -1).map((stop, i) => {
                const next = stopsForSegments[i + 1]!;
                const fromStop = stop as TelematicsDeliveryStop;
                const toStop = next as TelematicsDeliveryStop;
                const mapSegmentIds = mapSegmentIdsBetweenVisibleStops(
                  routeContextSegments as any[],
                  fromStop,
                  toStop,
                );
                const segmentId =
                  mapSegmentIds[0] ??
                  buildRouteSegmentSelectionId(
                    row.vehicleId,
                    fromStop.day_date || row.date,
                    fromStop.stop_number,
                    toStop.stop_number,
                    i,
                  );
                let distance = 0;
                const fromIdx = routeContextStops.indexOf(fromStop);
                const toIdx = routeContextStops.indexOf(toStop);
                if (fromIdx >= 0 && toIdx > fromIdx) {
                  for (let j = fromIdx; j < toIdx; j++) {
                    distance += (routeContextStops[j] as TelematicsDeliveryStop)?.miles_to_next_stop ?? 0;
                  }
                } else {
                  distance = fromStop.miles_to_next_stop ?? 0;
                }
                return {
                  id: segmentId,
                  mapSegmentIds: mapSegmentIds.length > 0 ? mapSegmentIds : [segmentId],
                  from_stop_number: fromStop.stop_number,
                  to_stop_number: toStop.stop_number,
                  distance,
                  label: 'Segment',
                };
              });
            })();

        // Total: sum segment miles when we have segments; fallback to telematics daily_miles (or range sum for week/month)
        const totalDistance = segmentsForSidebar.length > 0
          ? segmentsForSidebar.reduce((sum, s) => sum + (s.distance ?? 0), 0)
          : (isDateRange ? telematicsMilesForRange : (telematicsMilesRecord?.daily_miles ?? 0));
        const mapModalStdVid = standardiseVehicleId(row.vehicleId) || row.vehicleId || '';
        const mapModalFuelCostPerMile = config
          ? getFuelCostPerMile(resolveVehicleCostClassLabelLocal(mapModalStdVid), config)
          : 0;
        const mapModalRouteFuelCost = totalDistance * mapModalFuelCostPerMile;

        const unmatchedHandoffs: SyntheticOrderHandoffStop[] = (() => {
          const list: OrderHandoffMapPoint[] = order && orderHandoffPoint
            ? [orderHandoffPoint]
            : vehicleDateOrderHandoffs;
          return list.map((h) => ({
            stopId: `order-handoff-${h.orderId}`,
            orderId: h.orderId,
            lat: h.lat,
            lon: h.lon,
            label: h.label,
            postcode: h.postcode,
          }));
        })();
        const playbackOrderRankMap = (() => {
          const track = buildVehicleDayPlaybackTrack(visibleStopsForMap, sortedSegments);
          if (!track) return new Map<string, number>();
          const ordered = withOrderProximityEvents(track, {
            deliveries: deliveryDestinationsForStops,
            handoffs: unmatchedHandoffs.map((h) => ({
              orderId: h.orderId,
              lat: h.lat,
              lon: h.lon,
              label: h.label,
              postcode: h.postcode,
            })),
          });
          const map = new Map<string, number>();
          for (const event of ordered.events) {
            const match = event.id.match(/(?:order-delivery-proximity-|order-handoff-proximity-)(.+)$/);
            if (!match) continue;
            map.set(match[1], event.t);
          }
          return map;
        })();
        /** Vehicle-map optimiser waypoints (Start/End fixed; extras proximity-ordered). */
        const suggestStopsForVehicle = order
          ? []
          : buildVehicleSuggestStops({
              visibleStops: visibleStopsForMap,
              extras: [
                ...unmatchedDeliveries.map((d) => ({
                  orderId: d.orderId,
                  latitude: d.lat,
                  longitude: d.lon,
                  postcode: d.postcode,
                  label: [d.postcode, d.city].filter(Boolean).join(' • ') || `Delivery ${d.orderId.slice(0, 8)}`,
                })),
                ...unmatchedHandoffs.map((h) => ({
                  orderId: h.orderId,
                  latitude: h.lat,
                  longitude: h.lon,
                  postcode: h.postcode,
                  label: [h.label, h.postcode].filter(Boolean).join(' • ') || `Handoff ${h.orderId.slice(0, 8)}`,
                })),
              ],
              proximityOrderMap: playbackOrderRankMap,
            });
        const currentSuggestCrowFliesMiles = haversinePathMiles(suggestStopsForVehicle);
        const suggestedOrderIndices = Array.isArray(suggestedRouteResult?.order)
          ? suggestedRouteResult.order
          : [];
        const suggestedCrowFliesMiles = suggestedRouteResult
          ? haversinePathMiles(suggestStopsForVehicle, suggestedRouteResult.order)
          : 0;
        const crowFliesSavingsMiles = Math.max(0, currentSuggestCrowFliesMiles - suggestedCrowFliesMiles);
        const sidebarStopRows = order
          ? [
              ...(orderMapStartPoint ? [{ kind: 'telematics' as const, stop: { stop_type: 'Start', stop_number: 1, AssetName: orderMapStartVehicle, day_date: orderMapStartDate, postcode: 'START', latitude: orderMapStartPoint.lat, longitude: orderMapStartPoint.lon, miles_to_next_stop: 0, stop_id: 'order-start' } as any, stopId: 'order-start' }] : []),
              ...(orderHandoffPoint ? [{ kind: 'order_handoff' as const, handoff: { stopId: 'order-handoff', orderId: order.orderId, lat: orderHandoffPoint.lat, lon: orderHandoffPoint.lon, label: orderHandoffPoint.label, postcode: orderHandoffPoint.postcode } }] : []),
              ...(orderDeliveryPoint ? [{ kind: 'order_delivery' as const, delivery: { stopId: 'order-delivery', orderId: order.orderId, lat: orderDeliveryPoint.lat, lon: orderDeliveryPoint.lon, postcode: order.destination_postal_code, city: order.destination_city } }] : []),
            ]
          : buildMapSidebarStopRows(
              sortedStops,
              unmatchedHandoffs,
              unmatchedDeliveries,
              deliveryStopIds,
              playbackOrderRankMap,
            );

        // Determine stop positions

        const modalBounds = (() => {
          // When opened from an order row with no route data, center on order destination (or origin)
          if (order) {
            if (modalStops.length === 0 && modalSegments.length === 0) {
              if (orderDeliveryPoint) {
                return { lat: orderDeliveryPoint.lat, lon: orderDeliveryPoint.lon, zoom: 12 };
              }
              if (orderHandoffPoint) {
                return { lat: orderHandoffPoint.lat, lon: orderHandoffPoint.lon, zoom: 12 };
              }
              if (orderPickupPoint) {
                return { lat: orderPickupPoint.lat, lon: orderPickupPoint.lon, zoom: 12 };
              }
            }
          }
          // Try to calculate bounds from stops first
          if (modalStops.length > 0) {
            const lats = modalStops.map(s => s.latitude).filter(lat => lat != null && lat !== 0);
            const lons = modalStops.map(s => s.longitude).filter(lon => lon != null && lon !== 0);
            if (lats.length > 0 && lons.length > 0) {
              const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
              const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
              return { lat: centerLat, lon: centerLon, zoom: 12 };
            }
          }
          // If no stops, try to calculate bounds from route segments
          if (modalSegments.length > 0) {
            const allCoords: [number, number][] = [];
            modalSegments.forEach(seg => {
              if (seg.points && Array.isArray(seg.points)) {
                seg.points.forEach(([lat, lon]: [number, number]) => {
                  if (lat != null && lon != null && lat !== 0 && lon !== 0) {
                    allCoords.push([lat, lon]);
                  }
                });
              }
            });
            if (allCoords.length > 0) {
              const lats = allCoords.map(([lat]) => lat);
              const lons = allCoords.map(([, lon]) => lon);
              const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
              const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
              return { lat: centerLat, lon: centerLon, zoom: 12 };
            }
          }
          // If no stops/segments but we have order pins (opened from Daily Summary), center on them
          if (modalStops.length === 0 && modalSegments.length === 0) {
            const pinPts = [
              ...vehicleDateOrderDestinations.map((d) => ({ lat: d.lat, lon: d.lon })),
              ...vehicleDateOrderHandoffs.map((h) => ({ lat: h.lat, lon: h.lon })),
            ];
            if (pinPts.length > 0) {
              const lats = pinPts.map((d) => d.lat);
              const lons = pinPts.map((d) => d.lon);
              const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
              const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
              return { lat: centerLat, lon: centerLon, zoom: 12 };
            }
          }
          // Fallback to UK center if no data available
          return { lat: 54.7024, lon: -3.2766, zoom: 6 };
        })();
        return (
          <div className="fixed inset-0 z-dropdown flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-popover rounded-lg border border-border shadow-2xl max-w-7xl w-full max-h-screen-90 overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <div className="min-w-0">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-success shrink-0" />
                    Vehicle Map: {row.vehicleId}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                    {order ? (
                      <>
                        <span>{row.periodLabel || row.date}</span>
                        <span className="text-muted-foreground">·</span>
                        <button
                          type="button"
                          onClick={() => {
                            const oid = order.orderId || '';
                            if (!oid) return;
                            navigator.clipboard.writeText(oid).then(() => {
                              setCopiedOrderId(oid);
                              setTimeout(() => setCopiedOrderId(null), 1500);
                            }).catch(() => {});
                          }}
                          className="text-foreground hover:text-chart-5 hover:underline cursor-pointer focus:outline-none focus:ring-1 focus:ring-chart-5 rounded text-left font-mono break-all"
                          title={`Click to copy: ${order.orderId || ''}`}
                        >
                          Order {order.orderId || '—'}
                        </button>
                        {copiedOrderId === (order.orderId || '') && <span className="text-success text-xs shrink-0">Copied!</span>}
                      </>
                    ) : (
                      (row.periodLabel || row.date)
                    )}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-accent rounded-lg transition-colors shrink-0"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>
              
              <div className="flex-1 overflow-hidden flex min-h-125 h-screen-70">
                {/* Route Summary Sidebar - Left */}
                <div className="w-85 min-w-85 bg-card border-r border-border overflow-y-auto flex-shrink-0">
                  <div className="p-4">
                    <h4 className="text-sm font-bold text-white text-center mb-4">Route Summary</h4>

                    {/* Route efficiency — Phase 1: current miles + fuel cost for this route (no backend optimisation yet) */}
                    <div className="mb-6 p-3 rounded-lg bg-secondary/50 border border-border">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Route efficiency</p>
                      <div className="space-y-1 text-sm">
                        <div className="text-white">
                          Total distance: <span className="font-semibold text-foreground">{totalDistance.toFixed(1)} mi</span>
                        </div>
                        <div className="text-warning/90">
                          Fuel cost (this route):{' '}
                          <span className="font-semibold text-warning">
                            £{mapModalRouteFuelCost.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Based on telematics miles × £/mile (vehicle type from Configuration).</p>
                    </div>

                    {/* Suggest better route — vehicle day only (order map is single-order; hide for now) */}
                    {!order && suggestStopsForVehicle.length >= 2 && (
                      <div className="mb-6">
                        {!suggestedRouteResult ? (
                          <button
                            type="button"
                            disabled={suggestRouteLoading}
                            onClick={() => {
                              setSuggestRouteLoading(true);
                              setSuggestedRouteResult(null);
                              window.setTimeout(() => {
                                const result = suggestRouteOrder(suggestStopsForVehicle);
                                setSuggestedRouteResult(result);
                                setSuggestRouteLoading(false);
                              }, 50);
                            }}
                            className="group w-full py-2.5 px-3 rounded-lg bg-primary disabled:bg-muted disabled:cursor-not-allowed text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
                          >
                            {suggestRouteLoading ? (
                              <>Calculating…</>
                            ) : (
                              <>
                                <Lightbulb className="w-4 h-4 shrink-0 transition-transform duration-500 group-hover:scale-150" />
                                Suggest better route
                              </>
                            )}
                          </button>
                        ) : (
                          <div className="p-3 rounded-lg bg-secondary/50 border border-success/50 space-y-3">
                            <p className="text-xs font-semibold text-success uppercase tracking-wide">Suggested stop order</p>
                            <p className="text-xs text-muted-foreground">
                              Crow-flies total: <span className="font-semibold text-white">{suggestedCrowFliesMiles.toFixed(1)} mi</span>
                              <span className="text-muted-foreground"> (great-circle, not road miles)</span>
                              {crowFliesSavingsMiles > 0.05 && (
                                <span className="block mt-1 text-warning">
                                  Crow-flies improvement vs current stop order: {crowFliesSavingsMiles.toFixed(1)} mi
                                  {' '}(~£{(crowFliesSavingsMiles * mapModalFuelCostPerMile).toFixed(2)} at current £/mi — indicative only)
                                </span>
                              )}
                            </p>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {suggestedOrderIndices.length > 0 && suggestedOrderIndices.map((origIdx, listPos) => {
                                const stop = suggestStopsForVehicle[origIdx];
                                const segMi = Array.isArray(suggestedRouteResult?.segmentMiles)
                                  ? suggestedRouteResult.segmentMiles[listPos]
                                  : undefined;
                                const stopLabel = stop
                                  ? (stop.label || stop.postcode || `Stop ${stop.stop_number ?? origIdx}`)
                                  : `Stop ${origIdx}`;
                                return (
                                  <div key={`sug-${origIdx}-${listPos}`} className="flex items-center gap-2 text-xs text-foreground">
                                    <span className="text-muted-foreground w-5">{listPos + 1}.</span>
                                    <span>{stopLabel}</span>
                                    {segMi != null && <span className="text-success/90 ml-auto">→ {segMi.toFixed(1)} mi</span>}
                                  </div>
                                );
                              })}
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground">
                              <input
                                type="checkbox"
                                checked={showSuggestedOnMap}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setShowSuggestedOnMap(checked);
                                  if (!checked) {
                                    setSuggestedRoadGeometry(null);
                                    setSuggestedRoadRouteError(false);
                                    setSuggestedRoadRouteHint(null);
                                    setAllowStraightLineFallback(true);
                                    return;
                                  }
                                  const coords = suggestStopCoordinatesForMap(
                                    suggestStopsForVehicle,
                                    suggestedOrderIndices,
                                  );
                                  if (coords.length < 2) return;
                                  setSuggestedRoadRouteLoading(true);
                                  setSuggestedRoadRouteError(false);
                                  setSuggestedRoadRouteHint(null);
                                  setSuggestedRoadGeometry(null);
                                  setAllowStraightLineFallback(false);
                                  const straightLineTimeoutId = window.setTimeout(() => setAllowStraightLineFallback(true), 3000);
                                  authenticatedFetch('/api/route/driving', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ coordinates: coords }),
                                    credentials: 'include',
                                  })
                                    .then((r) => r.json())
                                    .then((data) => {
                                      if (data?.ok && Array.isArray(data?.geometry?.coordinates) && data.geometry.coordinates.length >= 2) {
                                        setSuggestedRoadGeometry(data.geometry.coordinates);
                                      } else {
                                        setSuggestedRoadRouteError(true);
                                        setSuggestedRoadRouteHint(data?.hint ?? null);
                                        setAllowStraightLineFallback(true);
                                      }
                                    })
                                    .catch(() => { setSuggestedRoadRouteError(true); setSuggestedRoadRouteHint(null); })
                                    .finally(() => {
                                      setSuggestedRoadRouteLoading(false);
                                      window.clearTimeout(straightLineTimeoutId);
                                      setAllowStraightLineFallback(true);
                                    });
                                }}
                                className="rounded border-border bg-secondary text-success focus:ring-success"
                                disabled={suggestedRoadRouteLoading}
                              />
                              Show suggested route on map
                            </label>
                            {suggestedRoadRouteLoading && <span className="text-xs text-warning">Loading road route…</span>}
                            {suggestedRoadRouteError && !suggestedRoadGeometry?.length && (
                              <div className="text-xs text-warning space-y-1">
                                <p>Road route unavailable; showing straight lines.</p>
                                {suggestedRoadRouteHint && (
                                  <p className="text-muted-foreground mt-1">{suggestedRoadRouteHint}</p>
                                )}
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => { setSuggestedRouteResult(null); setShowSuggestedOnMap(false); setSuggestedRoadGeometry(null); setSuggestedRoadRouteError(false); setSuggestedRoadRouteHint(null); setAllowStraightLineFallback(true); }}
                              className="w-full py-1.5 text-xs text-muted-foreground hover:text-white border border-border hover:border-border rounded transition-colors"
                            >
                              Clear suggestion
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ORDERS section - when opened from Daily Summary (green MapPin): role-aware pickup / handoff / delivery */}
                    {!order && (row.orderIds?.length ?? 0) > 0 && (() => {
                      const orderIds = row.orderIds ?? [];
                      const recordsById = new Map(orders.map(r => [r.orderId, r]));
                      const handoffByOrder = new Map(vehicleDateOrderHandoffs.map((h) => [h.orderId, h]));
                      const ordersForSidebar = orderIds
                        .map(id => recordsById.get(id))
                        .filter((r): r is ProfitMapOrder => r != null);
                      const roleCountByKey = ordersForSidebar.reduce(
                        (acc, ord) => {
                          const role = orderRoleOnVehicleMap(ord, modalVehicleForRole, pickupOrderIdSet, deliveryOrderIdSet);
                          if (role === 'pickup') acc.pickup += 1;
                          if (role === 'delivery') acc.delivery += 1;
                          return acc;
                        },
                        { pickup: 0, delivery: 0 },
                      );
                      const pickupCount = roleCountByKey.pickup;
                      const deliveryCount = roleCountByKey.delivery;
                      const visibleOrdersForSidebar = ordersForSidebar.filter((ord) => {
                        const role = orderRoleOnVehicleMap(ord, modalVehicleForRole, pickupOrderIdSet, deliveryOrderIdSet);
                        if (sidebarOrderRoleFilter === 'all') return true;
                        return role === sidebarOrderRoleFilter;
                      });
                      if (ordersForSidebar.length === 0) return null;
                      const anyOrderHasCoords = ordersForSidebar.some((ord) => {
                        const role = orderRoleOnVehicleMap(ord, modalVehicleForRole, pickupOrderIdSet, deliveryOrderIdSet);
                        if (role === 'pickup') return handoffByOrder.has(ord.orderId) || !!orderGeocodeMapPoint(ord, 'origin');
                        return !!orderGeocodeMapPoint(ord, 'destination') || !!orderGeocodeMapPoint(ord, 'origin');
                      });
                      return (
                        <div className="mb-6">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            {([
                              { key: 'pickup', label: 'TRUNK', count: pickupCount, activeClass: 'border-chart-10 bg-chart-10/15 text-chart-10', inactiveClass: 'border-border bg-card/50 text-foreground hover:border-border hover:text-white' },
                              { key: 'delivery', label: 'DELIVERY', count: deliveryCount, activeClass: 'border-chart-2 bg-chart-2/15 text-chart-2', inactiveClass: 'border-border bg-card/50 text-foreground hover:border-border hover:text-white' },
                            ] as const).map((filter) => {
                              const isActive = sidebarOrderRoleFilter === filter.key;
                              return (
                                <button
                                  key={filter.key}
                                  type="button"
                                  onClick={() => setSidebarOrderRoleFilter((prev) => prev === filter.key ? 'all' : filter.key)}
                                  className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                                    isActive ? filter.activeClass : filter.inactiveClass
                                  }`}
                                >
                                  <span>{filter.label}</span>
                                  <span className="rounded bg-black/20 px-1.5 py-0.5 text-base font-bold leading-none min-w-6 text-center">
                                    x {filter.count}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          {!anyOrderHasCoords && (
                            <p className="text-xs text-warning/90 mb-2">
                              {orderCoordsPending
                                ? 'Order locations are still loading on the server — refresh in a minute.'
                                : 'Order locations missing — ensure orders have postcodes, then refresh (or re-run Order compute after geocode).'}
                            </p>
                          )}
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {visibleOrdersForSidebar.length === 0 ? (
                              <p className="text-xs text-muted-foreground py-2">No orders match the selected filter.</p>
                            ) : visibleOrdersForSidebar.map((ord) => {
                              const isExpanded = expandedOrderIdInMapSidebar === ord.orderId;
                              const role = orderRoleOnVehicleMap(ord, modalVehicleForRole, pickupOrderIdSet, deliveryOrderIdSet);
                              const destPt = role === 'delivery' ? orderGeocodeMapPoint(ord, 'destination') : null;
                              const originPt = orderGeocodeMapPoint(ord, 'origin');
                              const handoff = role === 'pickup' ? handoffByOrder.get(ord.orderId) : undefined;
                              const deliveryVehicle = standardiseVehicleId(ord.vehicle_observed ?? ord.vehicleId ?? '');
                              return (
                                <div key={ord.orderId} className="rounded border border-border bg-secondary/40 overflow-hidden">
                                  <button
                                    type="button"
                                    onClick={() => setExpandedOrderIdInMapSidebar((prev) => (prev === ord.orderId ? null : ord.orderId))}
                                    className="w-full flex items-center gap-2 px-2 py-2 text-left font-mono text-sm text-foreground hover:bg-accent/50 focus:outline-none focus:ring-1 focus:ring-ring"
                                  >
                                    <span className="shrink-0 text-muted-foreground">{isExpanded ? '▼' : '▶'}</span>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void copyOrderIdToClipboard(ord.orderId);
                                      }}
                                      title={copiedTrackingIds.has(ord.orderId) ? 'Copied' : `Copy tracking ID: ${ord.orderId}`}
                                      className="truncate bg-transparent border-0 p-0 cursor-pointer text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-chart-5 rounded self-start w-fit max-w-full"
                                    >
                                      Order {ord.orderId}
                                      {copiedTrackingIds.has(ord.orderId) && <Check className="ml-1 w-3.5 h-3.5 text-success shrink-0 inline" aria-label="Copied" />}
                                    </button>
                                    {role === 'pickup' && (
                                      <span className="ml-auto shrink-0 text-xs uppercase tracking-wide text-chart-10/90">Pick-up</span>
                                    )}
                                  </button>
                                  {isExpanded && (
                                    <div className="px-2 pb-2 pt-0 space-y-2 border-t border-border">
                                      {originPt && (
                                        <div
                                          role="button"
                                          tabIndex={0}
                                          onClick={() => {
                                            if (mapOrderSpotSelected === `origin-${ord.orderId}`) {
                                              setMapOrderSpotSelected(null);
                                              setMapFlyToPoint(null);
                                              setMapFitBoundsTrigger(t => t + 1);
                                              return;
                                            }
                                            setMapOrderSpotSelected(`origin-${ord.orderId}`);
                                            setSelectedStopId(null);
                                            skipFlyToStopRef.current = true;
                                            if (selectedStopId) lastSkippedFlyToStopIdRef.current = selectedStopId;
                                            setMapFlyToPoint({ lat: originPt.lat, lon: originPt.lon });
                                          }}
                                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (e.target as HTMLElement).click(); } }}
                                          className="flex items-start gap-2 cursor-pointer hover:bg-accent/50 rounded px-1.5 py-1 -mx-1.5"
                                        >
                                          <Package className="w-3.5 h-3.5 text-chart-5 shrink-0 mt-0.5" fill={CHART_PALETTE[4]} stroke="white" strokeWidth={1.5} />
                                          <div className="min-w-0 flex-1">
                                            <p className="text-xs font-medium text-chart-5">Pickup</p>
                                            <p className="text-xs text-muted-foreground truncate">
                                              {[ord.origin_postal_code, ord.origin_city].filter(Boolean).join(' • ') || '—'}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                      {handoff && (
                                        <div
                                          role="button"
                                          tabIndex={0}
                                          onClick={() => {
                                            if (mapOrderSpotSelected === `handoff-${ord.orderId}`) {
                                              setMapOrderSpotSelected(null);
                                              setMapFlyToPoint(null);
                                              setMapFitBoundsTrigger(t => t + 1);
                                              return;
                                            }
                                            setMapOrderSpotSelected(`handoff-${ord.orderId}`);
                                            setSelectedStopId(null);
                                            skipFlyToStopRef.current = true;
                                            if (selectedStopId) lastSkippedFlyToStopIdRef.current = selectedStopId;
                                            setMapFlyToPoint({ lat: handoff.lat, lon: handoff.lon });
                                          }}
                                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (e.target as HTMLElement).click(); } }}
                                          className="flex items-start gap-2 cursor-pointer hover:bg-accent/50 rounded px-1.5 py-1 -mx-1.5"
                                        >
                                          <HandoffMapIcon size="sm" />
                                          <div className="min-w-0 flex-1">
                                            <p className="text-xs font-medium text-chart-10">Handoff</p>
                                            <p className="text-xs text-muted-foreground truncate">
                                              {[handoff.label, handoff.postcode].filter(Boolean).join(' • ') || '—'}
                                            </p>
                                            {deliveryVehicle ? (
                                              <p className="text-xs text-muted-foreground mt-0.5">Delivery by {deliveryVehicle}</p>
                                            ) : null}
                                          </div>
                                        </div>
                                      )}
                                      {destPt && (
                                        <div
                                          role="button"
                                          tabIndex={0}
                                          onClick={() => {
                                            if (mapOrderSpotSelected === `dest-${ord.orderId}`) {
                                              setMapOrderSpotSelected(null);
                                              setMapFlyToPoint(null);
                                              setMapFitBoundsTrigger(t => t + 1);
                                              return;
                                            }
                                            setMapOrderSpotSelected(`dest-${ord.orderId}`);
                                            setSelectedStopId(null);
                                            skipFlyToStopRef.current = true;
                                            if (selectedStopId) lastSkippedFlyToStopIdRef.current = selectedStopId;
                                            setMapFlyToPoint({ lat: destPt.lat, lon: destPt.lon });
                                          }}
                                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (e.target as HTMLElement).click(); } }}
                                          className="flex items-start gap-2 cursor-pointer hover:bg-accent/50 rounded px-1.5 py-1 -mx-1.5"
                                        >
                                          <Target className="w-3.5 h-3.5 text-chart-2 shrink-0 mt-0.5" fill={CHART_PALETTE[1]} stroke="white" strokeWidth={1.5} />
                                          <div className="min-w-0 flex-1">
                                            <p className="text-xs font-medium text-chart-2">Delivery</p>
                                            <p className="text-xs text-muted-foreground truncate">
                                              {[ord.destination_postal_code, ord.destination_city?.trim().toLowerCase() === 'uk' ? 'UK' : ord.destination_city].filter(Boolean).join(' • ') || '—'}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                      {!originPt && !handoff && !destPt && (
                                        <p className="text-xs text-muted-foreground">No location data for this vehicle&apos;s leg</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {/* ORDER JOURNEY section - above SEGMENTS when modal opened from order row; cards are clickable to zoom map */}
                    {order && (
                      <div className="mb-6">
                        <h5 className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">ORDER JOURNEY</h5>
                        {((order as ProfitMapOrder).goods_quantity != null && String((order as ProfitMapOrder).goods_quantity).trim() !== '') || ((order as ProfitMapOrder).goods_weight != null && String((order as ProfitMapOrder).goods_weight).trim() !== '') ? (
                          <p className="text-xs text-muted-foreground mb-2">
                            {(order as ProfitMapOrder).goods_quantity != null && String((order as ProfitMapOrder).goods_quantity).trim() !== '' && (
                              <span>Quantity: {String((order as ProfitMapOrder).goods_quantity).trim()}</span>
                            )}
                            {(order as ProfitMapOrder).goods_quantity != null && String((order as ProfitMapOrder).goods_quantity).trim() !== '' && (order as ProfitMapOrder).goods_weight != null && String((order as ProfitMapOrder).goods_weight).trim() !== '' && <span className="mx-1">·</span>}
                            {(order as ProfitMapOrder).goods_weight != null && String((order as ProfitMapOrder).goods_weight).trim() !== '' && (
                              <span>Weight: {String((order as ProfitMapOrder).goods_weight).trim()} kg</span>
                            )}
                          </p>
                        ) : null}
                        <div className="space-y-3">
                          {/* Pickup - click to fly to origin; click again to deselect and zoom back */}
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              if (mapOrderSpotSelected === 'pickup') {
                                setMapOrderSpotSelected(null);
                                setMapFlyToPoint(null);
                                setMapFitBoundsTrigger(t => t + 1);
                                return;
                              }
                              if (orderPickupPoint) {
                                setMapOrderSpotSelected('pickup');
                                setSelectedStopId(null);
                                skipFlyToStopRef.current = true;
                                if (selectedStopId) lastSkippedFlyToStopIdRef.current = selectedStopId;
                                setMapFlyToPoint({ lat: orderPickupPoint.lat, lon: orderPickupPoint.lon });
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (mapOrderSpotSelected === 'pickup') {
                                  setMapOrderSpotSelected(null);
                                  setMapFlyToPoint(null);
                                  setMapFitBoundsTrigger(t => t + 1);
                                  return;
                                }
                                if (orderPickupPoint) {
                                  setMapOrderSpotSelected('pickup');
                                  setSelectedStopId(null);
                                  skipFlyToStopRef.current = true;
                                  if (selectedStopId) lastSkippedFlyToStopIdRef.current = selectedStopId;
                                  setMapFlyToPoint({ lat: orderPickupPoint.lat, lon: orderPickupPoint.lon });
                                }
                              }
                            }}
                            className={`rounded px-2 py-2 bg-secondary/40 border cursor-pointer transition-colors ${mapOrderSpotSelected === 'pickup' ? 'border-chart-5 bg-chart-5/20' : 'border-chart-5/30 hover:bg-accent/50 hover:border-chart-5/50'}`}
                          >
                            <div className="flex items-start gap-2">
                              <Package className="w-4 h-4 text-chart-5 shrink-0 mt-0.5" fill={CHART_PALETTE[4]} stroke="white" strokeWidth={1.5} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-chart-5">Pickup</p>
                                <p className="text-xs text-foreground flex justify-between items-baseline gap-2">
                                  <span>
                                    {[order.origin_postal_code, order.origin_city].filter(Boolean).join(' • ') || '—'}
                                  </span>
                                  <span className="text-muted-foreground shrink-0">{getDateOnly(order.origin_end_timestamp_local || order.date || '') || '—'}</span>
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {orderCollectedByLabel(order).startsWith('Depot ') ? 'Collected by' : 'Vehicle'}:{' '}
                                  {(() => {
                                    const c = orderCollectedByLabel(order);
                                    return c ? (c.startsWith('Depot ') ? c : standardiseVehicleId(c)) : '—';
                                  })()}
                                </p>
                              </div>
                            </div>
                          </div>
                          {/* Multi-leg: Pickup → Handoff → Delivery. Single-leg: Pickup → Delivery. */}
                          {orderHandoffPoint && (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              if (mapOrderSpotSelected === 'handoff') {
                                setMapOrderSpotSelected(null);
                                setMapFlyToPoint(null);
                                setMapFitBoundsTrigger(t => t + 1);
                                return;
                              }
                              setMapOrderSpotSelected('handoff');
                              setSelectedStopId(null);
                              skipFlyToStopRef.current = true;
                              if (selectedStopId) lastSkippedFlyToStopIdRef.current = selectedStopId;
                              setMapFlyToPoint({ lat: orderHandoffPoint.lat, lon: orderHandoffPoint.lon });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (mapOrderSpotSelected === 'handoff') {
                                  setMapOrderSpotSelected(null);
                                  setMapFlyToPoint(null);
                                  setMapFitBoundsTrigger(t => t + 1);
                                  return;
                                }
                                setMapOrderSpotSelected('handoff');
                                setSelectedStopId(null);
                                skipFlyToStopRef.current = true;
                                if (selectedStopId) lastSkippedFlyToStopIdRef.current = selectedStopId;
                                setMapFlyToPoint({ lat: orderHandoffPoint.lat, lon: orderHandoffPoint.lon });
                              }
                            }}
                            className={`rounded px-2 py-2 bg-secondary/40 border cursor-pointer transition-colors ${mapOrderSpotSelected === 'handoff' ? 'border-chart-10 bg-chart-10/20' : 'border-chart-10/30 hover:bg-accent/50 hover:border-chart-10/50'}`}
                          >
                            <div className="flex items-start gap-2">
                              <HandoffMapIcon size="sm" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-chart-10">Handoff</p>
                                <p className="text-xs text-foreground flex justify-between items-baseline gap-2">
                                  <span>
                                    {[orderHandoffPoint.label, orderHandoffPoint.postcode].filter(Boolean).join(' • ') || '—'}
                                  </span>
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Delivery by {standardiseVehicleId(order.vehicle_observed || order.vehicleId || '') || '—'}
                                </p>
                              </div>
                            </div>
                          </div>
                          )}
                          {orderDeliveryPoint && (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              if (mapOrderSpotSelected === 'delivery') {
                                setMapOrderSpotSelected(null);
                                setMapFlyToPoint(null);
                                setMapFitBoundsTrigger(t => t + 1);
                                return;
                              }
                              setMapOrderSpotSelected('delivery');
                              setSelectedStopId(null);
                              skipFlyToStopRef.current = true;
                              if (selectedStopId) lastSkippedFlyToStopIdRef.current = selectedStopId;
                              setMapFlyToPoint({ lat: orderDeliveryPoint.lat, lon: orderDeliveryPoint.lon });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (mapOrderSpotSelected === 'delivery') {
                                  setMapOrderSpotSelected(null);
                                  setMapFlyToPoint(null);
                                  setMapFitBoundsTrigger(t => t + 1);
                                  return;
                                }
                                setMapOrderSpotSelected('delivery');
                                setSelectedStopId(null);
                                skipFlyToStopRef.current = true;
                                if (selectedStopId) lastSkippedFlyToStopIdRef.current = selectedStopId;
                                setMapFlyToPoint({ lat: orderDeliveryPoint.lat, lon: orderDeliveryPoint.lon });
                              }
                            }}
                            className={`rounded px-2 py-2 bg-secondary/40 border cursor-pointer transition-colors ${mapOrderSpotSelected === 'delivery' ? 'border-chart-2 bg-chart-2/20' : 'border-chart-2/30 hover:bg-accent/50 hover:border-chart-2/50'}`}
                          >
                            <div className="flex items-start gap-2">
                              <Target className="w-4 h-4 text-chart-2 shrink-0 mt-0.5" fill={CHART_PALETTE[1]} stroke="white" strokeWidth={1.5} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-warning">Delivery</p>
                                <p className="text-xs text-foreground flex justify-between items-baseline gap-2">
                                  <span>
                                    {[order.destination_postal_code, order.destination_city?.trim().toLowerCase() === 'uk' ? 'UK' : order.destination_city].filter(Boolean).join(' • ') || '—'}
                                  </span>
                                  <span className="text-muted-foreground shrink-0">{getDateOnly(order.destination_end_timestamp_local || order.date || '') || '—'}</span>
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                  {deliveredByOtherDepot
                                    ? `Delivered by: Depot ${order.delivered_by_depot}`
                                    : `Vehicle: ${standardiseVehicleId(order.delivered_by_vehicle || order.vehicle_observed || order.vehicleId || '')}`}
                                  {(order.vehicle_observed && order.vehicle_pickup && order.vehicle_observed === order.vehicle_pickup) && (
                                    <span className="text-success" title="Same vehicle for pick-up and delivery"><Check className="w-3 h-3 inline" /></span>
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Segments Section — distances in miles (order map: GPS polyline km→mi) */}
                    {segmentsForSidebar.length > 0 && (
                      <div className="mb-6">
                        <h5 className="text-xs font-bold text-white uppercase tracking-wide mb-2">SEGMENTS:</h5>
                        <div className="space-y-1">
                          {segmentsForSidebar.map((seg, idx) => {
                            const isSelected = selectedSegmentId === seg.id;
                            const label = order
                              ? (seg.label || (idx === 0 ? 'Trunk leg' : 'Delivery leg'))
                              : `Seg ${idx + 1}`;
                            return (
                              <div 
                                key={seg.id}
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedSegmentId(null);
                                    setSelectedMapSegmentIds(null);
                                  } else {
                                    setSelectedSegmentId(seg.id);
                                    setSelectedMapSegmentIds(new Set(seg.mapSegmentIds ?? [seg.id]));
                                  }
                                  setSelectedStopId(null); // Clear stop selection when selecting a segment
                                  setMapOrderSpotSelected(null);
                                }}
                                className={`flex items-center gap-2 text-sm cursor-pointer transition-colors rounded px-2 py-1 ${
                                  isSelected 
                                    ? 'bg-ring/30 text-ring border border-ring' 
                                    : 'text-foreground hover:bg-secondary/50'
                                }`}
                              >
                                <span className="text-muted-foreground">-</span>
                                <span>{order ? label : `Seg ${idx + 1}`} : {seg.distance.toFixed(1)} mi</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Stops: Start/End + order-matched deliveries / handoffs (GPS-dwell intermediates hidden) */}
                    {sidebarStopRows.length > 0 && (
                      <div>
                        <h5 className="text-xs font-bold text-white uppercase tracking-wide mb-2">STOPS:</h5>
                        <div className="space-y-1">
                          {(() => {
                            type HandoffGroupEntry = {
                              key: string;
                              firstStopId: string;
                              place: string;
                              orderIds: string[];
                              lat: number;
                              lon: number;
                            };

                            const handoffGroups = new Map<string, HandoffGroupEntry>();
                            for (const row of sidebarStopRows) {
                              if (row.kind !== 'order_handoff') continue;
                              const { handoff } = row;
                              const key = `${handoff.label ?? ''}|${handoff.postcode ?? ''}|${handoff.lat ?? ''}|${handoff.lon ?? ''}`;
                              const existing = handoffGroups.get(key);
                              if (existing) {
                                existing.orderIds.push(handoff.orderId);
                                continue;
                              }
                              handoffGroups.set(key, {
                                key,
                                firstStopId: handoff.stopId,
                                place: [handoff.label, handoff.postcode].filter(Boolean).join(' • '),
                                orderIds: [handoff.orderId],
                                lat: handoff.lat,
                                lon: handoff.lon,
                              });
                            }

                            const groupedEntries = Array.from(handoffGroups.values());
                            const hasDuplicateHandoffLocations = groupedEntries.some((entry) => entry.orderIds.length > 1);

                            const renderHandoffEntry = (entry: HandoffGroupEntry, isGroup = false) => {
                              const isSelected = selectedStopId === entry.firstStopId;
                              const isCollapsed = collapsedHandoffGroups[entry.key] ?? true;

                              return (
                                <div
                                  key={entry.key}
                                  onClick={() => {
                                    if (isSelected) {
                                      setSelectedStopId(null);
                                      setMapFlyToPoint(null);
                                      setMapFitBoundsTrigger((t) => t + 1);
                                      return;
                                    }
                                    setSelectedStopId(entry.firstStopId);
                                    setSelectedSegmentId(null);
                                    setSelectedMapSegmentIds(null);
                                    setMapOrderSpotSelected(null);
                                    skipFlyToStopRef.current = true;
                                    setMapFlyToPoint({ lat: entry.lat, lon: entry.lon });
                                  }}
                                  className={`text-sm cursor-pointer transition-colors rounded px-2 py-1 pl-4 flex flex-col gap-0.5 ${
                                    isSelected
                                      ? 'bg-success/30 text-success border border-success'
                                      : 'bg-chart-10/20 text-chart-10 border border-chart-10/50 hover:bg-chart-10/30'
                                  }`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    {isGroup ? (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCollapsedHandoffGroups((prev) => ({
                                            ...prev,
                                            [entry.key]: !(prev[entry.key] ?? true),
                                          }));
                                        }}
                                        className="flex items-center justify-center text-chart-10 hover:text-white w-5 h-5"
                                        aria-label={isCollapsed ? 'Expand handoff group' : 'Collapse handoff group'}
                                      >
                                        <span className="text-base leading-none">{isCollapsed ? '▸' : '▾'}</span>
                                      </button>
                                    ) : null}
                                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                      <HandoffMapIcon size="sm" />
                                      <span>Handoff</span>
                                      {entry.place ? (
                                        <span className="text-xs ml-1 shrink-0 text-chart-10/90">({entry.place})</span>
                                      ) : null}
                                      {isGroup && (
                                        <span className="ml-auto text-xs font-bold text-chart-10 bg-chart-10/15 border border-chart-10/30 rounded px-1.5 py-0.5">
                                          x {entry.orderIds.length}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {!isGroup || !isCollapsed ? (
                                    <div className="pl-6 text-xs opacity-90">
                                      {isGroup ? (
                                        <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
                                          {entry.orderIds.map((orderId, index) => (
                                            <React.Fragment key={`${entry.key}-${orderId}`}>
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  void copyOrderIdToClipboard(orderId);
                                                }}
                                                title={copiedTrackingIds.has(orderId) ? 'Copied' : `Copy tracking ID: ${orderId}`}
                                                className="inline-flex w-fit max-w-full items-center gap-1 bg-transparent border-0 p-0 cursor-pointer hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-chart-5 rounded text-left self-start"
                                              >
                                                <span>{orderId}</span>
                                                {copiedTrackingIds.has(orderId) && <Check className="w-3.5 h-3.5 text-success shrink-0" aria-label="Copied" />}
                                              </button>
                                              {index < entry.orderIds.length - 1 && <span className="text-muted-foreground">,</span>}
                                            </React.Fragment>
                                          ))}
                                        </div>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            void copyOrderIdToClipboard(entry.orderIds[0]);
                                          }}
                                          title={copiedTrackingIds.has(entry.orderIds[0]) ? 'Copied' : `Copy tracking ID: ${entry.orderIds[0]}`}
                                          className="inline-flex w-fit max-w-full items-center gap-1 bg-transparent border-0 p-0 cursor-pointer hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-chart-5 rounded text-left self-start"
                                        >
                                          <span>{entry.orderIds[0]}</span>
                                          {copiedTrackingIds.has(entry.orderIds[0]) && <Check className="w-3.5 h-3.5 text-success shrink-0" aria-label="Copied" />}
                                        </button>
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            };

                            const rows: React.ReactNode[] = [];
                            const seenHandoffGroupKeys = new Set<string>();

                            for (const stopRow of sidebarStopRows) {
                              if (stopRow.kind === 'order_handoff') {
                                const handoffKey = `${stopRow.handoff.label ?? ''}|${stopRow.handoff.postcode ?? ''}|${stopRow.handoff.lat ?? ''}|${stopRow.handoff.lon ?? ''}`;
                                if (hasDuplicateHandoffLocations) {
                                  const groupEntry = groupedEntries.find((entry) => entry.key === handoffKey);
                                  if (!groupEntry || seenHandoffGroupKeys.has(groupEntry.key)) continue;
                                  seenHandoffGroupKeys.add(groupEntry.key);
                                  rows.push(renderHandoffEntry(groupEntry, groupEntry.orderIds.length > 1));
                                  continue;
                                }
                                rows.push(renderHandoffEntry({
                                  key: stopRow.handoff.stopId,
                                  firstStopId: stopRow.handoff.stopId,
                                  place: [stopRow.handoff.label, stopRow.handoff.postcode].filter(Boolean).join(' • '),
                                  orderIds: [stopRow.handoff.orderId],
                                  lat: stopRow.handoff.lat,
                                  lon: stopRow.handoff.lon,
                                }, false));
                                continue;
                              }

                              if (stopRow.kind === 'order_delivery') {
                                const { delivery } = stopRow;
                                const isSelected = selectedStopId === delivery.stopId;
                                const pc = (delivery.postcode ?? '').trim();
                                const city = delivery.city?.trim().toLowerCase() === 'uk' ? 'UK' : (delivery.city ?? '').trim();
                                const placeName = city || (pc ? 'Delivery' : '');
                                rows.push(
                                  <div
                                    key={delivery.stopId}
                                    onClick={() => {
                                      if (isSelected) {
                                        setSelectedStopId(null);
                                        setMapFlyToPoint(null);
                                        setMapFitBoundsTrigger((t) => t + 1);
                                        return;
                                      }
                                      setSelectedStopId(delivery.stopId);
                                      setSelectedSegmentId(null);
                                      setSelectedMapSegmentIds(null);
                                      setMapOrderSpotSelected(null);
                                      skipFlyToStopRef.current = true;
                                      setMapFlyToPoint({ lat: delivery.lat, lon: delivery.lon });
                                    }}
                                    className={`text-sm cursor-pointer transition-colors rounded px-2 py-1 pl-4 flex flex-col gap-0.5 ${
                                      isSelected
                                        ? 'bg-success/30 text-success border border-success'
                                        : 'bg-chart-2/20 text-chart-2 border border-chart-2/50 hover:bg-chart-2/30'
                                    }`}
                                  >
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <Target className="w-3.5 h-3.5 text-chart-2 shrink-0" fill={CHART_PALETTE[1]} stroke="white" strokeWidth={1.5} aria-hidden />
                                      <span>Delivery</span>
                                      {placeName ? <span className="text-xs text-chart-2/80">{placeName}</span> : null}
                                      {pc ? (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            void copyTextToClipboard(pc);
                                          }}
                                          title={copiedTrackingIds.has(pc) ? 'Copied' : `Copy postcode: ${pc}`}
                                          className="inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-chart-5 rounded text-xs ml-1 shrink-0 text-warning/90"
                                        >
                                          <span>{pc}</span>
                                          {copiedTrackingIds.has(pc) && <Check className="w-3.5 h-3.5 text-success shrink-0" aria-label="Copied" />}
                                        </button>
                                      ) : null}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void copyOrderIdToClipboard(delivery.orderId);
                                      }}
                                      title={copiedTrackingIds.has(delivery.orderId) ? 'Copied' : `Copy tracking ID: ${delivery.orderId}`}
                                      className="inline-flex w-fit max-w-full items-center gap-1 bg-transparent border-0 p-0 cursor-pointer hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-chart-5 rounded pl-6 text-left text-xs opacity-90 self-start"
                                    >
                                      <span>{delivery.orderId}</span>
                                      {copiedTrackingIds.has(delivery.orderId) && <Check className="w-3.5 h-3.5 text-success shrink-0" aria-label="Copied" />}
                                    </button>
                                  </div>
                                );
                                continue;
                              }

                              const { stop, stopId } = stopRow;
                              const isSelected = selectedStopId === stopId;
                              const isDeliveryStop = deliveryStopIds.has(stopId);
                              const deliveryOrderIds = deliveryStopToOrderIds.get(stopId) ?? [];
                              const rowClassName = isSelected
                                ? 'bg-success/30 text-success border border-success'
                                : isDeliveryStop
                                  ? 'bg-chart-2/20 text-chart-2 border border-chart-2/50 hover:bg-chart-2/30'
                                  : 'text-foreground hover:bg-secondary/50';
                              const hasTyped = sortedStops.some(s => s.stop_type === 'Start' || s.stop_type === 'End');
                              const isStart = hasTyped ? stop.stop_type === 'Start' : stop === sortedStops[0];
                              const isEnd   = hasTyped ? stop.stop_type === 'End'   : stop === sortedStops[sortedStops.length - 1];
                              const anchorStops = sortedStops.filter((s) => isTelematicsRouteAnchor(s, sortedStops));
                              const baseLabel = order
                                ? (isStart ? buildOrderRouteLegLabel('start') : isEnd ? buildOrderRouteLegLabel('end') : 'Leg checkpoint')
                                : (isStart || isEnd
                                  ? buildRouteAnchorText(
                                      stop,
                                      isStart,
                                      anchorStops,
                                      row.vehicleId,
                                      (order as any)?.vehicle_pickup ?? null,
                                      (order as any)?.vehicle_observed ?? (order as any)?.vehicleId ?? row.vehicleId ?? null,
                                    )
                                  : `Stop ${stop.stop_number}`);
                              const N = deliveryOrderIds.length;
                              const hasOrderIds = isDeliveryStop && N > 0;
                              rows.push(
                                <div 
                                  key={stopId}
                                  onClick={() => {
                                    setSelectedStopId(isSelected ? null : stopId);
                                    setSelectedSegmentId(null);
                                    setSelectedMapSegmentIds(null);
                                    setMapOrderSpotSelected(null);
                                  }}
                                  className={`text-sm cursor-pointer transition-colors rounded px-2 py-1 pl-4 flex flex-col gap-0.5 ${rowClassName}`}
                                >
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    {isStart ? <PlayCircle className="w-3.5 h-3.5 text-success shrink-0" fill={STATUS_COLORS.success} stroke="white" strokeWidth={1.5} /> : isEnd ? <Flag className="w-3.5 h-3.5 text-destructive shrink-0" fill={STATUS_COLORS.destructive} stroke="white" strokeWidth={1.5} /> : <span className={`w-3.5 h-3.5 shrink-0 block ${isDeliveryStop ? 'text-chart-2' : 'text-muted-foreground'}`} aria-hidden />}
                                    {!isDeliveryStop ? (
                                      <span>{baseLabel}</span>
                                    ) : N > 1 ? (
                                      <span className="whitespace-nowrap">{baseLabel} - Delivery <strong className="font-bold">x {N}</strong></span>
                                    ) : (
                                      <span>{baseLabel} - Delivery</span>
                                    )}
                                    {stop.postcode && stop.stop_type !== 'Start' && stop.stop_type !== 'End' && stop.postcode !== 'START' && stop.postcode !== 'END' && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          void copyTextToClipboard(stop.postcode);
                                        }}
                                        title={copiedTrackingIds.has(stop.postcode) ? 'Copied' : `Copy postcode: ${stop.postcode}`}
                                        className={`inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-chart-5 rounded text-xs ml-1 shrink-0 ${isDeliveryStop ? 'text-warning/90' : 'text-muted-foreground'}`}
                                      >
                                        <span>{stop.postcode}</span>
                                        {copiedTrackingIds.has(stop.postcode) && <Check className="w-3.5 h-3.5 text-success shrink-0" aria-label="Copied" />}
                                      </button>
                                    )}
                                  </div>
                                  {hasOrderIds && (
                                    <div className="pl-6 text-xs opacity-90">
                                      <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
                                        {deliveryOrderIds.map((orderId, index) => (
                                          <React.Fragment key={`${stopId}-${orderId}`}>
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                void copyOrderIdToClipboard(orderId);
                                              }}
                                              title={copiedTrackingIds.has(orderId) ? 'Copied' : `Copy tracking ID: ${orderId}`}
                                              className="inline-flex w-fit max-w-full items-center gap-1 bg-transparent border-0 p-0 cursor-pointer hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-chart-5 rounded text-left self-start"
                                            >
                                              <span>{orderId}</span>
                                              {copiedTrackingIds.has(orderId) && <Check className="w-3.5 h-3.5 text-success shrink-0" aria-label="Copied" />}
                                            </button>
                                            {index < deliveryOrderIds.length - 1 && <span className="text-muted-foreground">,</span>}
                                          </React.Fragment>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            return rows;
                          })()}
                        </div>
                      </div>
                    )}

                  </div>
                </div>

                {/* Map - Right */}
                <div className="flex-1 overflow-hidden relative min-h-125">
                  {modalStops.length === 0 && modalSegments.length === 0 ? (
                    <div className="w-full h-full flex items-center justify-center bg-card">
                      <div className="text-center px-6">
                        <MapPin className="w-12 h-12 text-accent mx-auto mb-2" />
                        {telematicsLoadError ? (
                          <>
                            <p className="text-warning/90">Could not load telematics for this day</p>
                            <p className="text-muted-foreground text-sm mt-1">{telematicsLoadError}</p>
                          </>
                        ) : (
                          <>
                            <p className="text-muted-foreground">No delivery stops or route data available</p>
                            <p className="text-muted-foreground text-sm mt-1">
                              No GPS data found for {row.vehicleId} on {row.periodLabel || row.date}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full relative bg-card">
                      <div className="w-full h-full">
                        <MapComponent
                          center={[modalBounds.lon, modalBounds.lat]}
                          zoom={modalBounds.zoom}
                          theme="dark"
                        >
                          <MapController
                            stops={modalStops}
                            segments={modalSegments}
                            mapDataKey={`${row.vehicleId}|${startDate}|${endDate}`}
                            orderPoints={orderPoints}
                            fittedKeyRef={mapModalFittedKeyRef}
                            flyToPoint={mapFlyToPoint}
                            onFlyToDone={() => setMapFlyToPoint(null)}
                            skipFlyToStopRef={skipFlyToStopRef}
                            lastSkippedFlyToStopIdRef={lastSkippedFlyToStopIdRef}
                            fitBoundsTrigger={mapFitBoundsTrigger}
                            lastProcessedFitBoundsTriggerRef={lastProcessedFitBoundsTriggerRef}
                          />
                          <MapControls
                            position="bottom-right"
                            showZoom
                            showCompass
                            showFullscreen
                          />
                          {/* Inside Map so it stacks above the MapLibre canvas (sibling overlays get covered) */}
                          <div className="absolute bottom-3 left-3 z-30 w-map-overlay pointer-events-auto">
                            <VehicleMapGpsPlayback
                              stops={order ? modalStops : visibleStopsForMap}
                              segments={order ? modalSegments : sortedSegments}
                              resetKey={`${row.vehicleId}|${startDate}|${endDate}|${order?.orderId ?? 'vehicle-day'}`}
                              orderJourneyMode={Boolean(order)}
                              orderDeliveries={deliveryDestinationsForStops}
                              orderHandoffs={unmatchedHandoffs.map((h) => ({
                                orderId: h.orderId,
                                lat: h.lat,
                                lon: h.lon,
                                label: h.label,
                                postcode: h.postcode,
                              }))}
                              onPositionChange={handleMapPlaybackPosition}
                            />
                          </div>
                          {mapPlaybackPos && (
                            <MapMarker longitude={mapPlaybackPos.lon} latitude={mapPlaybackPos.lat}>
                              <MarkerContent>
                                <div className="relative flex items-center justify-center drop-shadow-lg">
                                  <Truck className="w-7 h-7 text-chart-10" strokeWidth={2.5} fill={CHART_PALETTE[9]} stroke="white" aria-hidden />
                                </div>
                              </MarkerContent>
                              <MarkerPopup className="vehicle-tooltip" closeButton={true}>
                                <div className="px-2 py-1.5 space-y-0.5">
                                  <p className="font-medium text-chart-10">Vehicle position</p>
                                  <p className="text-xs text-foreground tabular-nums">
                                    <span className="text-muted-foreground">{formatUkPlaybackDate(mapPlaybackPos.timestamp)}</span>
                                    <span className="ml-1.5">{formatUkPlaybackClock(mapPlaybackPos.timestamp)}</span>
                                    <span className="ml-1 text-xs uppercase text-muted-foreground">UK</span>
                                  </p>
                                </div>
                              </MarkerPopup>
                            </MapMarker>
                          )}
                          {routeContextSegments.length > 0 && (
                            <div className="absolute top-2 right-2 z-10 flex flex-col gap-2 max-w-50">
                              <div className="rounded-md border border-border bg-popover/95 px-2.5 py-2 text-xs text-foreground shadow">
                                <p className="font-semibold text-foreground mb-1.5">Map legend</p>
                                <ul className="space-y-1">
                                  {order ? (
                                    <>
                                      <li className="flex items-center gap-2">
                                        <span className="w-3 h-0.5 bg-chart-1 rounded shrink-0" aria-hidden />
                                        <span>{deliveredByOtherDepot ? 'Collection / trunk leg' : 'Trunk leg'}</span>
                                      </li>
                                      {deliveredByOtherDepot ? (
                                      <li className="flex items-center gap-2">
                                        <Target className="w-3.5 h-3.5 text-amber-400 shrink-0" aria-hidden />
                                        <span>{`Delivered by Depot ${order?.delivered_by_depot}`}</span>
                                      </li>
                                      ) : (
                                      <li className="flex items-center gap-2">
                                        <span className="w-3 h-0.5 bg-chart-2 rounded shrink-0" aria-hidden />
                                        <span>Delivery leg</span>
                                      </li>
                                      )}
                                      {orderPickupPoint && (
                                      <li className="flex items-center gap-2">
                                        <Package className="w-3.5 h-3.5 text-chart-5 shrink-0" fill={CHART_PALETTE[4]} stroke="white" strokeWidth={1.5} aria-hidden />
                                        <span>Pickup (collection)</span>
                                      </li>
                                      )}
                                      {orderMapStartPoint && (
                                      <li className="flex items-center gap-2">
                                        <PlayCircle className="w-3.5 h-3.5 text-success shrink-0" fill={STATUS_COLORS.success} stroke="white" strokeWidth={1} aria-hidden />
                                        <span>Start (Trunk starting point)</span>
                                      </li>
                                      )}
                                      {orderHandoffPoint && (
                                      <li className="flex items-center gap-2">
                                        <HandoffMapIcon size="sm" />
                                        <span>Handoff (hub)</span>
                                      </li>
                                      )}
                                      {orderDeliveryPoint && (
                                      <li className="flex items-center gap-2">
                                        <Target className="w-3.5 h-3.5 text-chart-2 shrink-0" fill={CHART_PALETTE[1]} stroke="white" strokeWidth={1.5} aria-hidden />
                                        <span>Delivery (destination)</span>
                                      </li>
                                      )}
                                      {mapPlaybackPos && (
                                      <li className="flex items-center gap-2">
                                        <Truck className="w-3.5 h-3.5 text-chart-10 shrink-0" strokeWidth={2.5} fill={CHART_PALETTE[9]} stroke="white" aria-hidden />
                                        <span>Replay position</span>
                                      </li>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <li className="flex items-center gap-2">
                                        {mapPlaybackPos ? (
                                          <span className="flex items-center gap-0.5 shrink-0" aria-hidden>
                                            <span className="w-2 h-0.5 bg-chart-1/40 rounded" />
                                            <span className="w-2.5 h-0.5 bg-chart-1 rounded" />
                                          </span>
                                        ) : (
                                          <span className="w-3 h-0.5 bg-chart-1 rounded shrink-0" aria-hidden />
                                        )}
                                        <span>
                                          {mapPlaybackPos
                                            ? 'Actual route · replay progress'
                                            : 'Actual route (telematics)'}
                                        </span>
                                      </li>
                                      {orderDeliveryPoint && (
                                      <li className="flex items-center gap-2">
                                        <Target className="w-3.5 h-3.5 text-chart-2 shrink-0" fill={CHART_PALETTE[1]} stroke="white" strokeWidth={1.5} aria-hidden />
                                        <span>Delivery destination</span>
                                      </li>
                                      )}
                                      {!order && vehicleDateOrderDestinations.length > 0 && (
                                      <li className="flex items-center gap-2">
                                        <Target className="w-3.5 h-3.5 text-chart-2 shrink-0" fill={CHART_PALETTE[1]} stroke="white" strokeWidth={1.5} aria-hidden />
                                        <span>Delivery destination</span>
                                      </li>
                                      )}
                                      {(orderHandoffPoint || (!order && vehicleDateOrderHandoffs.length > 0)) && (
                                      <li className="flex items-center gap-2">
                                        <HandoffMapIcon size="sm" />
                                        <span>Handoff (hub)</span>
                                      </li>
                                      )}
                                      <li className="flex items-center gap-2">
                                        <PlayCircle className="w-3.5 h-3.5 text-success shrink-0" fill={STATUS_COLORS.success} stroke="white" strokeWidth={1} aria-hidden />
                                        <span>Route start</span>
                                      </li>
                                      <li className="flex items-center gap-2">
                                        <Flag className="w-3.5 h-3.5 text-destructive shrink-0" fill={STATUS_COLORS.destructive} stroke="white" strokeWidth={1} aria-hidden />
                                        <span>Route end</span>
                                      </li>
                                      {showSuggestedOnMap && suggestedRouteResult && (
                                        <li className="flex items-center gap-2">
                                          <span className="w-3 h-0.5 bg-success rounded shrink-0" aria-hidden />
                                          <span>Suggested optimised route</span>
                                        </li>
                                      )}
                                      {order && orderPickupPoint && (
                                        <li className="flex items-center gap-2">
                                          <Package className="w-3.5 h-3.5 text-chart-5 shrink-0" fill={CHART_PALETTE[4]} stroke="white" strokeWidth={1.5} aria-hidden />
                                          <span>Order pickup</span>
                                        </li>
                                      )}
                                      {mapPlaybackPos && (
                                        <li className="flex items-center gap-2">
                                          <Truck className="w-3.5 h-3.5 text-chart-10 shrink-0" strokeWidth={2.5} fill={CHART_PALETTE[9]} stroke="white" aria-hidden />
                                          <span>Replay position</span>
                                        </li>
                                      )}
                                    </>
                                  )}
                                </ul>
                              </div>
                            </div>
                          )}
                          {/* Delivery Target marker — only when this vehicle is the delivery leg */}
                          {order && orderDeliveryPoint && (
                              <MapMarker longitude={orderDeliveryPoint.lon} latitude={orderDeliveryPoint.lat}>
                                <MarkerContent>
                                  <div className="relative cursor-pointer flex items-center justify-center">
                                    <Target className="w-7 h-7 text-chart-2 drop-shadow-lg" fill={CHART_PALETTE[1]} stroke="white" strokeWidth={2} />
                                  </div>
                                </MarkerContent>
                                <MarkerPopup className="vehicle-tooltip" closeButton={true}>
                                  <div className="px-2 py-1.5 space-y-1">
                                    <p className="font-medium text-chart-2">Order delivery destination</p>
                                    <p className="text-xs text-muted-foreground">
                                      Attributed to this vehicle via order assignment
                                    </p>
                                    <p className="text-xs text-foreground">Order #{(order.orderId || '').slice(0, 8)}…</p>
                                    {order.destination_coords_source === 'postcode_centroid' && (
                                      <p className="text-xs text-muted-foreground">
                                        Postcode centre — not the doorstep address
                                      </p>
                                    )}
                                    {order.destination_city && <p className="text-xs text-foreground">{order.destination_city.trim().toLowerCase() === 'uk' ? 'UK' : order.destination_city}</p>}
                                  </div>
                                </MarkerPopup>
                              </MapMarker>
                          )}
                          {order && orderHandoffPoint && (
                              <MapMarker longitude={orderHandoffPoint.lon} latitude={orderHandoffPoint.lat}>
                                <MarkerContent>
                                  <div className="relative cursor-pointer flex items-center justify-center drop-shadow-lg">
                                    <HandoffMapIcon size="lg" />
                                  </div>
                                </MarkerContent>
                                <MarkerPopup className="vehicle-tooltip" closeButton={true}>
                                  <div className="px-2 py-1.5 space-y-1">
                                    <p className="font-medium text-chart-10">Handoff</p>
                                    <p className="text-xs text-muted-foreground">{orderHandoffPoint.label}</p>
                                    <p className="text-xs text-foreground">Order #{(order.orderId || '').slice(0, 8)}…</p>
                                  </div>
                                </MarkerPopup>
                              </MapMarker>
                          )}
                          {!order && vehicleDateOrderDestinations.length > 0 && vehicleDateOrderDestinations.map((dest) => (
                            <MapMarker key={`delivery-hit-${dest.orderId}`} longitude={dest.lon} latitude={dest.lat}>
                              <MarkerContent>
                                <div className="relative cursor-pointer flex items-center justify-center">
                                  <Target className="w-6 h-6 text-chart-2 drop-shadow-lg" fill={CHART_PALETTE[1]} stroke="white" strokeWidth={2} />
                                </div>
                              </MarkerContent>
                              <MarkerPopup className="vehicle-tooltip" closeButton={true}>
                                <div className="px-2 py-1.5 space-y-1">
                                  <p className="font-medium text-chart-2">Order delivery destination</p>
                                  <p className="text-xs text-muted-foreground">
                                    Attributed to this vehicle via order assignment
                                  </p>
                                  <p className="text-xs text-foreground font-mono">Order {dest.orderId}</p>
                                  {dest.approximate && (
                                    <p className="text-xs text-muted-foreground">
                                      Postcode centre — not the doorstep address
                                    </p>
                                  )}
                                  {(dest.postcode || dest.city) && (
                                    <p className="text-xs text-foreground">
                                      {[dest.postcode, dest.city?.trim().toLowerCase() === 'uk' ? 'UK' : dest.city].filter(Boolean).join(' · ') || '—'}
                                    </p>
                                  )}
                                </div>
                              </MarkerPopup>
                              <MarkerTooltip className="vehicle-tooltip">
                                <div className="px-2 py-1.5">
                                  <p className="text-xs font-medium text-chart-2">Delivery destination</p>
                                  <p className="text-xs text-muted-foreground">{dest.orderId.slice(0, 12)}{dest.orderId.length > 12 ? '…' : ''}</p>
                                </div>
                              </MarkerTooltip>
                            </MapMarker>
                          ))}
                          {!order && vehicleDateOrderHandoffs.map((h) => (
                            <MapMarker key={`handoff-hit-${h.orderId}`} longitude={h.lon} latitude={h.lat}>
                              <MarkerContent>
                                <div className="relative cursor-pointer flex items-center justify-center drop-shadow-lg">
                                  <HandoffMapIcon size="md" />
                                </div>
                              </MarkerContent>
                              <MarkerPopup className="vehicle-tooltip" closeButton={true}>
                                <div className="px-2 py-1.5 space-y-1">
                                  <p className="font-medium text-chart-10">Handoff</p>
                                  <p className="text-xs text-muted-foreground">{h.label}</p>
                                  <p className="text-xs text-foreground font-mono">Order {h.orderId}</p>
                                </div>
                              </MarkerPopup>
                              <MarkerTooltip className="vehicle-tooltip">
                                <div className="px-2 py-1.5">
                                  <p className="text-xs font-medium text-chart-10">Handoff</p>
                                  <p className="text-xs text-muted-foreground">{h.label}</p>
                                </div>
                              </MarkerTooltip>
                            </MapMarker>
                          ))}
                          {(order ? modalSegments : sortedSegments).map((seg: any, idx: number) => {
                            const coordinates = order
                              ? segmentCoordinatesForMapRoute(seg)
                              : (Array.isArray(seg?.points)
                                ? seg.points
                                    .filter(([lat, lon]: [number, number]) => lat != null && lon != null && lat !== 0 && lon !== 0)
                                    .map(([lat, lon]: [number, number]) => [lon, lat] as [number, number])
                                : []);
                            if (coordinates.length < 2) return null;
                            const segmentId = order
                              ? orderMapSegmentSelectionId(seg, idx)
                              : buildRouteSegmentSelectionId(
                                (seg as any).vehicle,
                                (seg as any).date,
                                (seg as any).from_stop_number,
                                (seg as any).to_stop_number,
                                idx,
                              );
                            const isSelected = selectedMapSegmentIds
                              ? selectedMapSegmentIds.has(segmentId)
                              : selectedSegmentId === segmentId;
                            const anySegSelected = Boolean(selectedSegmentId || (selectedMapSegmentIds && selectedMapSegmentIds.size > 0));
                            const playbackActive = mapPlaybackPos != null;
                            return (
                              <MapRoute
                                key={`modal-route-${segmentId}`}
                                id={segmentId}
                                coordinates={coordinates}
                                color={
                                  isSelected
                                    ? STATUS_COLORS.warning
                                    : order
                                      ? (seg.label === 'Delivery leg' ? CHART_PALETTE[1] : STATUS_COLORS.info)
                                      : STATUS_COLORS.info
                                }
                                width={isSelected ? 6 : 3}
                                opacity={
                                  order
                                    ? (isSelected ? 1 : anySegSelected ? 0.28 : 0.95)
                                    : playbackActive
                                      ? 0.28
                                      : showSuggestedOnMap && suggestedRouteResult
                                        ? 0.25
                                        : (isSelected ? 1 : anySegSelected ? 0.28 : 0.7)
                                }
                              />
                            );
                          })}
                          {/* GPS replay progress — same Actual Route blue, drawn on top of muted full-day path */}
                          {mapPlaybackPos && mapPlaybackPos.trail.length >= 2 && (
                            <MapRoute
                              id="gps-replay-trail"
                              coordinates={mapPlaybackPos.trail}
                              color={STATUS_COLORS.info}
                              width={5}
                              opacity={0.95}
                              interactive={false}
                            />
                          )}
                          {/* Suggested route overlay — vehicle map only; indices are into suggestStopsForVehicle */}
                          {!order && showSuggestedOnMap && suggestedRouteResult && suggestStopsForVehicle.length >= 2 && (
                            suggestedRoadGeometry && suggestedRoadGeometry.length >= 2 ? (
                              <MapRoute
                                key="suggested-route-road"
                                coordinates={suggestedRoadGeometry}
                                color={STATUS_COLORS.success}
                                width={4}
                                opacity={0.9}
                              />
                            ) : allowStraightLineFallback ? (
                              (() => {
                                const coords = suggestStopCoordinatesForMap(
                                  suggestStopsForVehicle,
                                  suggestedOrderIndices,
                                );
                                return coords.slice(0, -1).map((from, listPos) => {
                                  const to = coords[listPos + 1];
                                  if (!to) return null;
                                  return (
                                    <MapRoute
                                      key={`suggested-route-line-${listPos}`}
                                      coordinates={[from, to]}
                                      color={STATUS_COLORS.success}
                                      width={4}
                                      opacity={0.9}
                                    />
                                  );
                                });
                              })()
                            ) : null
                          )}
                          {/* Render start/end markers from route segments when no delivery stops exist */}
                          {modalStops.length === 0 && modalSegments.length > 0 && (() => {
                            // Get first and last valid GPS points from all segments
                            let firstPoint: [number, number] | null = null;
                            let lastPoint: [number, number] | null = null;
                            
                            for (let i = 0; i < modalSegments.length; i++) {
                              const seg = modalSegments[i];
                              if (!seg.points || !Array.isArray(seg.points)) continue;
                              const validPoints = seg.points.filter(([lat, lon]: [number, number]) => lat != null && lon != null && lat !== 0 && lon !== 0);
                              if (validPoints.length > 0) {
                                if (!firstPoint) firstPoint = [validPoints[0][0], validPoints[0][1]];
                                if (validPoints.length > 0) lastPoint = [validPoints[validPoints.length - 1][0], validPoints[validPoints.length - 1][1]];
                              }
                            }
                            
                            return (
                              <>
                                {firstPoint && (
                                  <MapMarker longitude={firstPoint[1]} latitude={firstPoint[0]}>
                                    <MarkerContent>
                                      <div className="relative cursor-pointer flex items-center justify-center">
                                        <PlayCircle 
                                          className="w-6 h-6 text-success drop-shadow-lg"
                                          fill={STATUS_COLORS.success}
                                          stroke="white"
                                          strokeWidth={1.5}
                                        />
                                      </div>
                                    </MarkerContent>
                                    <MarkerPopup className="vehicle-tooltip" closeButton={true}>
                                      <div className="px-2 py-1.5 space-y-1">
                                        <p className="font-medium text-white">Route Start</p>
                                        <p className="text-xs text-foreground">
                                          Lat: {firstPoint[0].toFixed(5)}
                                        </p>
                                        <p className="text-xs text-foreground">
                                          Lon: {firstPoint[1].toFixed(5)}
                                        </p>
                                      </div>
                                    </MarkerPopup>
                                  </MapMarker>
                                )}
                                {lastPoint && lastPoint !== firstPoint && (
                                  <MapMarker longitude={lastPoint[1]} latitude={lastPoint[0]}>
                                    <MarkerContent>
                                      <div className="relative cursor-pointer flex items-center justify-center">
                                        <Flag 
                                          className="w-6 h-6 text-destructive drop-shadow-lg"
                                          fill={STATUS_COLORS.destructive}
                                          stroke="white"
                                          strokeWidth={1.5}
                                        />
                                      </div>
                                    </MarkerContent>
                                    <MarkerPopup className="vehicle-tooltip" closeButton={true}>
                                      <div className="px-2 py-1.5 space-y-1">
                                        <p className="font-medium text-white">Route End</p>
                                        <p className="text-xs text-foreground">
                                          Lat: {lastPoint[0].toFixed(5)}
                                        </p>
                                        <p className="text-xs text-foreground">
                                          Lon: {lastPoint[1].toFixed(5)}
                                        </p>
                                      </div>
                                    </MarkerPopup>
                                  </MapMarker>
                                )}
                              </>
                            );
                          })()}
                          {/* Stop markers: Start/End + order-matched deliveries only (hide GPS-dwell intermediates) */}
                          {modalStops
                            .filter((s) => {
                              if (!s.latitude || !s.longitude || s.latitude === 0 || s.longitude === 0) return false;
                              if (isTelematicsRouteAnchor(s, sortedStops)) return true;
                              const id = `stop-${s.AssetName}-${s.day_date}-${s.stop_number}`;
                              return deliveryStopIds.has(id);
                            })
                            .sort((a, b) => {
                              const aId = `stop-${a.AssetName}-${a.day_date}-${a.stop_number}`;
                              const bId = `stop-${b.AssetName}-${b.day_date}-${b.stop_number}`;
                              if (aId === selectedStopId) return 1;
                              if (bId === selectedStopId) return -1;
                              return 0;
                            })
                            .map((stop, idx) => {
                              const isOrderDeliveryStop = deliveryStopIds.has(
                                `stop-${stop.AssetName}-${stop.day_date}-${stop.stop_number}`,
                              );
                              // Use stop_type when available (new backend), fall back to positional check (old data)
                              const hasTypedAnchors = sortedStops.some(s => s.stop_type === 'Start' || s.stop_type === 'End');
                              const isFirstStop = hasTypedAnchors
                                ? stop.stop_type === 'Start'
                                : stop.stop_number === sortedStops[0]?.stop_number;
                              const isLastStop = hasTypedAnchors
                                ? stop.stop_type === 'End'
                                : stop.stop_number === sortedStops[sortedStops.length - 1]?.stop_number;
                              const stopId = `stop-${stop.AssetName}-${stop.day_date}-${stop.stop_number}`;
                              const isSelected = selectedStopId === stopId;
                              
                              return (
                                <MapMarker
                                  key={`modal-stop-${stop.AssetName}-${stop.day_date}-${stop.stop_number}-${idx}`}
                                  longitude={stop.longitude}
                                  latitude={stop.latitude}
                                  onClick={() => {
                                    setSelectedStopId(isSelected ? null : stopId);
                                    setSelectedSegmentId(null); // Clear segment selection when selecting a stop
                                    setSelectedMapSegmentIds(null);
                                    setMapOrderSpotSelected(null);
                                  }}
                                >
                                  <MarkerContent>
                                    <div className={`relative cursor-pointer flex items-center justify-center ${isSelected ? 'animate-bounce' : ''}`} style={{ animationDuration: '0.6s' }}>
                                      {isFirstStop ? (
                                        <PlayCircle 
                                          className="w-6 h-6 text-success drop-shadow-lg"
                                          fill={STATUS_COLORS.success}
                                          stroke="white"
                                          strokeWidth={isSelected ? 2 : 1.5}
                                        />
                                      ) : isLastStop ? (
                                        <Flag 
                                          className="w-6 h-6 text-destructive drop-shadow-lg"
                                          fill={STATUS_COLORS.destructive}
                                          stroke="white"
                                          strokeWidth={isSelected ? 2 : 1.5}
                                        />
                                      ) : (
                                        <Target
                                          className="w-6 h-6 text-chart-2 drop-shadow-lg"
                                          strokeWidth={isSelected ? 2.5 : 2}
                                        />
                                      )}
                                    </div>
                                  </MarkerContent>
                                  {isSelected && (
                                    <MarkerPopup className="vehicle-tooltip" closeButton={true}>
                                      <div className="px-2 py-1.5 space-y-1">
                                        <p className="font-medium text-white">
                                          {stop.stop_type === 'Start' ? 'Start' : stop.stop_type === 'End' ? 'End' : isOrderDeliveryStop ? 'Delivery stop' : `Stop #${stop.stop_number}`} - {standardiseVehicleId(stop.AssetName)}
                                        </p>
                                        <p className="text-xs text-foreground">
                                          Date: {normalizeTelematicsDate(stop.day_date) || stop.day_date}
                                        </p>
                                        {stop.arrival_time && (
                                          <p className="text-xs text-foreground">
                                            Arrival: {new Date(stop.arrival_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                          </p>
                                        )}
                                        {stop.departure_time && (
                                          <p className="text-xs text-foreground">
                                            Departure: {new Date(stop.departure_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                          </p>
                                        )}
                                        <p className="text-xs text-foreground">
                                          Duration: {(stop.duration_minutes ?? 0).toFixed(1)} min
                                        </p>
                                        <p className="text-xs text-foreground">
                                          Postcode: {stop.postcode || '—'}
                                        </p>
                                        <p className="text-xs text-foreground">
                                          Type: <span className={isOrderDeliveryStop ? 'text-chart-2' : 'text-muted-foreground'}>{stop.stop_type || '—'}</span>
                                        </p>
                                        <p className="text-xs text-foreground">
                                          Miles to Next: {(stop.miles_to_next_stop ?? 0).toFixed(1)}
                                        </p>
                                      </div>
                                    </MarkerPopup>
                                  )}
                                  <MarkerTooltip className="vehicle-tooltip">
                                    <div className="px-2 py-1.5 space-y-1">
                                      <p className="font-medium text-white">
                                        {stop.stop_type === 'Start' ? 'Start' : stop.stop_type === 'End' ? 'End' : isOrderDeliveryStop ? 'Delivery stop' : `Stop #${stop.stop_number}`}
                                      </p>
                                      <p className="text-xs text-foreground">
                                        {stop.postcode || standardiseVehicleId(stop.AssetName)}
                                      </p>
                                    </div>
                                  </MarkerTooltip>
                                </MapMarker>
                              );
                            })}
                          {/* Order pickup (collection) — purple package at origin geocode; distinct from green Start */}
                          {order && orderPickupPoint && (
                                  <MapMarker
                                    longitude={orderPickupPoint.lon}
                                    latitude={orderPickupPoint.lat}
                                    onClick={() => {
                                      setMapOrderSpotSelected((prev) => (prev === 'pickup' ? null : 'pickup'));
                                      setSelectedStopId(null);
                                      setSelectedSegmentId(null);
                                      setSelectedMapSegmentIds(null);
                                      skipFlyToStopRef.current = true;
                                      if (selectedStopId) lastSkippedFlyToStopIdRef.current = selectedStopId;
                                      setMapFlyToPoint({ lat: orderPickupPoint.lat, lon: orderPickupPoint.lon });
                                    }}
                                  >
                                    <MarkerContent>
                                      <div className={`relative cursor-pointer flex items-center justify-center ${mapOrderSpotSelected === 'pickup' ? 'animate-bounce' : ''}`} style={{ animationDuration: '0.6s' }}>
                                        <Package className="w-7 h-7 text-chart-5 drop-shadow-lg" fill={CHART_PALETTE[4]} stroke="white" strokeWidth={2} />
                                      </div>
                                    </MarkerContent>
                                    <MarkerPopup className="vehicle-tooltip" closeButton={true}>
                                      <div className="px-2 py-1.5 space-y-1">
                                        <p className="font-medium text-chart-5">Pickup (collection)</p>
                                        <p className="text-xs text-foreground">Order #{(order.orderId || '').slice(0, 8)}…</p>
                                        {(order.origin_postal_code || order.origin_city) && (
                                          <p className="text-xs text-foreground">
                                            {[order.origin_postal_code, order.origin_city].filter(Boolean).join(' • ')}
                                          </p>
                                        )}
                                        {orderCollectedByLabel(order) && (
                                          <p className="text-xs text-muted-foreground">
                                            {orderCollectedByLabel(order).startsWith('Depot ')
                                              ? `Collected by: ${orderCollectedByLabel(order)}`
                                              : `Vehicle: ${standardiseVehicleId(orderCollectedByLabel(order))}`}
                                          </p>
                                        )}
                                      </div>
                                    </MarkerPopup>
                                    <MarkerTooltip className="vehicle-tooltip">
                                      <div className="px-2 py-1.5 space-y-1">
                                        <p className="font-medium text-chart-5">Pickup</p>
                                        <p className="text-xs text-foreground">
                                          {order.origin_postal_code
                                            || order.origin_city
                                            || (order.vehicle_pickup
                                              ? standardiseVehicleId(order.vehicle_pickup)
                                              : 'Collection')}
                                        </p>
                                      </div>
                                    </MarkerTooltip>
                                  </MapMarker>
                          )}
                        </MapComponent>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
};

export default ProfitVehicleDayMapModal;
